import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import BASE_URL from "../config";
import { Link } from "react-router-dom";
import HomeEventsSection from "../components/HomeEventsSection";

/* ═══════════════════════════════════════════════════════════════════════════
   CACI — Home / "Comptoir" edition
   ───────────────────────────────────────────────────────────────────────────
   Nothing was removed: every section, every API call, every i18n key and
   every route is exactly where it was. What changed is how it looks, how it
   moves, and — above all — how it behaves on a phone.

   WHAT'S NEW
   · Mobile-first layout. Hero uses svh (no more jump when the browser bar
     hides), the caption sits in the thumb zone, and slides respond to swipe.
   · Horizontal snap rails replace cramped one-column stacks for services and
     articles on phones, each with its own scroll indicator.
   · One orchestrated motion system: a single easing + duration scale, a
     scroll-reveal observer with per-item stagger, word-by-word hero titles.
   · The signature: a brass seal — the mark a chamber of commerce actually
     puts on a document — presses into place beside every section eyebrow.
   · prefers-reduced-motion is honoured everywhere; touch targets are ≥44px;
     focus rings are visible.

   NOTE  <HomeUltraStyle /> is intentionally no longer rendered. It shipped 82
   `!important` rules that would fight everything below. The file is still in
   /components if you ever want it back — just re-import and render it after
   <style>{GLOBAL_STYLES}</style>.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Motion: one scroll-reveal observer for the whole page ──────────────────
   Elements opt in with data-rv="rise|fade|left|right|zoom" and stagger with
   style={{ "--i": n }}. If anything at all goes wrong, every element is
   revealed immediately — content is never left invisible. */
function useReveal(deps = []) {
  const scopeRef = useRef(null);

  useEffect(() => {
    const scope = scopeRef.current || document;
    let io;
    const showAll = (els) => els.forEach((el) => el.classList.add("rv-in"));

    try {
      const els = Array.from(scope.querySelectorAll("[data-rv]:not(.rv-in)"));
      if (!els.length) return;

      const reduce =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (reduce || typeof IntersectionObserver === "undefined") {
        showAll(els);
        return;
      }

      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("rv-in");
            io.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -6% 0px", threshold: 0.06 }
      );
      els.forEach((el) => io.observe(el));
    } catch (e) {
      showAll(Array.from((scopeRef.current || document).querySelectorAll("[data-rv]")));
    }

    return () => io && io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return scopeRef;
}

/* ── Reading progress: a hairline of brass across the top of the viewport ── */
function useScrollProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    let raf = 0;
    const read = () => {
      raf = 0;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      setPct(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(read); };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return pct;
}

/* ── The signature mark ──────────────────────────────────────────────────────
   A chamber of commerce is the body that stamps and seals documents. This is
   that stamp, drawn in strokes: two concentric rings, a ring of ticks, and a
   centre dot. It presses in (scale down, settle) when its section arrives. */
function Seal({ size = 22, className = "", spin = false }) {
  const ticks = [];
  for (let i = 0; i < 24; i++) {
    const a = (i / 24) * Math.PI * 2;
    const r1 = 26, r2 = i % 2 === 0 ? 21 : 23.5;
    ticks.push(
      <line
        key={i}
        x1={32 + Math.cos(a) * r1}
        y1={32 + Math.sin(a) * r1}
        x2={32 + Math.cos(a) * r2}
        y2={32 + Math.sin(a) * r2}
      />
    );
  }
  return (
    <svg
      className={`seal${spin ? " seal-spin" : ""} ${className}`}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <circle cx="32" cy="32" r="30" opacity=".45" />
        <circle cx="32" cy="32" r="17" />
        <g opacity=".8">{ticks}</g>
      </g>
      <circle cx="32" cy="32" r="5" fill="currentColor" />
    </svg>
  );
}

/* ── Section header ──────────────────────────────────────────────────────────
   Fixes a long-standing bug: three sections passed the SAME i18n key to the
   eyebrow and to the <h2>, so the sentence printed twice. The eyebrow is
   dropped whenever it would just repeat the title. On phones the "see more"
   link becomes a full-width row instead of a 13px target in the corner. */
function SectionHead({ label, title, sub, action, tone = "light" }) {
  const norm = (s) => String(s || "").trim().toLowerCase();
  const showLabel = label && norm(label) !== norm(title);
  return (
    <header className={`sec-head sec-head--${tone}`}>
      <div className="sec-head-text">
        {showLabel && (
          <span className="sec-label" data-rv="fade">
            <Seal size={15} className="sec-label-seal" />
            {label}
          </span>
        )}
        <h2 className="sec-title" data-rv="rise" style={{ "--i": 1 }}>
          {title}
        </h2>
        {sub && (
          <p className="sec-sub" data-rv="rise" style={{ "--i": 2 }}>
            {sub}
          </p>
        )}
      </div>
      {action && (
        <div className="sec-head-action" data-rv="rise" style={{ "--i": 3 }}>
          {action}
        </div>
      )}
    </header>
  );
}

/* ── Rail ────────────────────────────────────────────────────────────────────
   On a phone this is a swipeable, snapping track with a live progress
   indicator — cards stay big and legible instead of being crushed into a
   column. From 992px up it becomes an ordinary grid and the rail behaviour
   disappears completely. */
function Rail({ children, columns = 3, className = "", label }) {
  const trackRef = useRef(null);
  const [pos, setPos] = useState(0);
  const [scrollable, setScrollable] = useState(false);

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setScrollable(max > 12);
    setPos(max > 0 ? Math.min(1, Math.abs(el.scrollLeft) / max) : 0);
  }, []);

  useEffect(() => {
    measure();
    const el = trackRef.current;
    if (!el) return;
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener("resize", measure);
    return () => {
      ro && ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, children]);

  return (
    <div className={`rail-shell ${className}`}>
      <div
        className="rail"
        ref={trackRef}
        onScroll={measure}
        style={{ "--cols": columns }}
        role="region"
        aria-label={label}
      >
        {children}
      </div>
      <div className={`rail-bar${scrollable ? " is-on" : ""}`} aria-hidden="true">
        <span style={{ insetInlineStart: `${pos * 62}%` }} />
      </div>
    </div>
  );
}
/* Every section's endpoint in one place, so adding a band to the page means
   adding one line here rather than editing the fetch logic. */
const SECTION_ENDPOINTS = {
  slides:      "/api/slide",
  features:    "/api/pages/features",
  services:    "/api/pages/services",
  articles:    "/api/articles/home",
  popup:       "/api/slide/popup",
  partenaires: "/api/slide/partenaire",
  chambres:    "/api/chambre_de_commerces_home",
};

/* ── Section states ──────────────────────────────────────────────────────────
   Each section owns its own loading, empty and failed state, so a slow or
   broken endpoint degrades that one band instead of holding the whole page
   hostage behind a spinner. The skeletons are built from the real card
   metrics, which is what keeps the layout from shifting when data lands. */

function Skel({ w, h, r, className = "", style }) {
  return (
    <span
      className={`skel ${className}`}
      style={{ width: w, height: h, borderRadius: r, ...style }}
      aria-hidden="true"
    />
  );
}

/* One card placeholder, shaped by whichever section it is rendered into. */
function SkelCard({ withBar = false }) {
  return (
    <div className="skel-card" aria-hidden="true">
      {withBar && <Skel className="skel-bar" />}
      <Skel className="skel-media" />
      <div className="skel-card-body">
        <Skel h={13} r={7} w="88%" />
        <Skel h={13} r={7} w="64%" />
        <Skel h={11} r={6} w="42%" style={{ marginBlockStart: 6 }} />
      </div>
    </div>
  );
}

function SkelCards({ count = 3, withBar = false }) {
  return Array.from({ length: count }, (_, i) => (
    <div key={i}><SkelCard withBar={withBar} /></div>
  ));
}

function SkelChambres({ count = 6 }) {
  return (
    <div className="rail" aria-hidden="true" style={{ "--cols": 6 }}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i}>
          <div className="skel-chambre">
            <Skel className="skel-ring" />
            <Skel h={12} r={6} w="72%" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkelHero() {
  return (
    <div className="hero-skel" aria-hidden="true">
      <div className="caci-shell">
        <div className="hero-skel-box">
          <Skel h={11} r={6} w={92} />
          <Skel className="skel-line" w="min(94%, 620px)" />
          <Skel className="skel-line" w="min(72%, 460px)" />
          <Skel h={14} r={7} w="min(60%, 380px)" style={{ marginBlockStart: 8 }} />
          <Skel h={54} r={999} w={188} style={{ marginBlockStart: 10 }} />
        </div>
      </div>
    </div>
  );
}

function SkelFeatures() {
  return (
    <div className="feat-layout" aria-hidden="true">
      <div className="skel-tabs">
        {Array.from({ length: 4 }, (_, i) => <Skel key={i} />)}
      </div>
      <div className="skel-pane">
        <Skel className="skel-pane-title" />
        <Skel className="skel-pane-media" />
        <div className="skel-pane-copy">
          <Skel /><Skel /><Skel /><Skel />
        </div>
      </div>
    </div>
  );
}

/* Empty and failed are written differently on purpose: an empty section is
   simply quiet, a failed one names what happened and offers the fix. */
function SectionNote({ state, onRetry, emptyText, errorText, retryText, variant }) {
  const isError = state === "error";
  return (
    <div className={variant === "hero" ? "hero-note" : "sec-note"} role="status">
      <Seal size={variant === "hero" ? 30 : 24} />
      <p>{isError ? errorText : emptyText}</p>
      {isError && onRetry && (
        <button type="button" className="sec-retry" onClick={onRetry}>
          <i className="bi bi-arrow-clockwise" />
          {retryText}
        </button>
      )}
    </div>
  );
}

/* ── Partner card ────────────────────────────────────────────────────────────
   Behaviour is unchanged from the original: a screenshot plus a lazy live
   mini-preview; hold the pointer for 3s (a brass bar fills under the chrome
   strip) or tap on touch, and the card flies out of the slider into a large
   centred panel showing the partner's real site. Pointer-out / Esc / ✕ /
   backdrop flies it back. Only the geometry was tuned so the panel fits a
   phone screen properly. */
function PartnerCard({ url, imgSrc, title, desc, displayUrl, ctaLabel, isRtl }) {
  const [mounted, setMounted] = useState(false);   // small in-card iframe mounted
  const [loaded, setLoaded] = useState(false);     // small iframe loaded
  const [expanded, setExpanded] = useState(false); // big panel mounted
  const [pState, setPState] = useState("idle");    // big panel iframe: idle | loading | live | failed
  const [mFailed, setMFailed] = useState(false);   // mini in-card iframe failed/blocked
  const [arming, setArming] = useState(false);     // 3s hover progress on the card

  const cardRef = useRef(null);
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  const intentTimer = useRef(null);
  const loadTimer = useRef(null);
  const pLoadTimer = useRef(null);
  const openedAt = useRef(0);
  const closing = useRef(false);
  const enteredPanel = useRef(false);

  const fine = typeof window !== "undefined" &&
    window.matchMedia && window.matchMedia("(pointer: fine)").matches;
  const reduceMotion = typeof window !== "undefined" &&
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ── small in-card preview (unchanged behaviour) ── */
  const handleEnter = () => {
    if (!mounted) setMounted(true);
    if (fine) {
      clearTimeout(intentTimer.current);
      setArming(true);
      intentTimer.current = setTimeout(openExpand, 3000); // hold 3s to expand
    }
  };
  const handleLeaveCard = () => {
    clearTimeout(intentTimer.current);
    setArming(false);
  };

  useEffect(() => {
    if (mounted && !loaded && !mFailed) {
      loadTimer.current = setTimeout(() => setMFailed(true), 6000);
    }
    return () => loadTimer.current && clearTimeout(loadTimer.current);
  }, [mounted, loaded, mFailed]);

  /* ── geometry helpers for the fly-out animation ── */
  const finalGeometry = () => {
    const vw = window.innerWidth, vh = window.innerHeight;
    const phone = vw < 768;
    const w = phone ? vw - 20 : Math.min(1120, vw * 0.94);
    const h = phone ? Math.min(vh - 24, vh * 0.92) : Math.min(760, vh * 0.84);
    return { top: (vh - h) / 2, left: (vw - w) / 2, width: w, height: h };
  };
  const applyGeo = (el, g) => {
    el.style.top = g.top + "px";
    el.style.left = g.left + "px";
    el.style.width = g.width + "px";
    el.style.height = g.height + "px";
  };

  const openExpand = () => {
    if (expanded || closing.current || !cardRef.current) return;
    openedAt.current = Date.now();
    enteredPanel.current = false;
    setArming(false);
    setPState(mFailed ? "failed" : "loading"); // known-blocked site → image only, no retry
    setExpanded(true);
    window.dispatchEvent(new CustomEvent("caci:partner-expand", { detail: { open: true } }));
  };

  // After the panel mounts: start at the card's rect, then animate to center.
  useEffect(() => {
    if (!expanded) return;
    const panel = panelRef.current, card = cardRef.current, back = backdropRef.current;
    if (!panel || !card) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    card.style.opacity = "0";
    card.style.pointerEvents = "none";

    const rect = card.getBoundingClientRect();
    applyGeo(panel, rect);
    panel.style.borderRadius = "20px";

    const settle = () => {
      applyGeo(panel, finalGeometry());
      panel.style.borderRadius = "22px";
      back && back.classList.add("is-open");
      panel.classList.add("is-open");
    };
    let raf1, raf2;
    if (reduceMotion) {
      panel.style.transition = "none";
      back && (back.style.transition = "none");
      settle();
    } else {
      raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(settle); });
    }

    const fallback = setTimeout(
      () => setPState((s) => (s === "live" ? s : "failed")),
      6000
    );
    pLoadTimer.current = fallback;

    const onKey = (e) => { if (e.key === "Escape") closeExpand(); };
    const onResize = () => { if (!closing.current) applyGeo(panel, finalGeometry()); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf1); cancelAnimationFrame(raf2);
      clearTimeout(fallback);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      document.body.style.overflow = prevOverflow;
      card.style.opacity = "";
      card.style.pointerEvents = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const closeExpand = () => {
    const panel = panelRef.current, card = cardRef.current, back = backdropRef.current;
    if (!expanded || closing.current || !panel || !card) return;
    closing.current = true;
    clearTimeout(intentTimer.current);

    const done = () => {
      closing.current = false;
      setExpanded(false);
      window.dispatchEvent(new CustomEvent("caci:partner-expand", { detail: { open: false } }));
    };

    if (reduceMotion) { done(); return; }
    back && back.classList.remove("is-open");
    panel.classList.remove("is-open");
    applyGeo(panel, card.getBoundingClientRect()); // fly back to wherever the card is now
    panel.style.borderRadius = "20px";
    setTimeout(done, 540);
  };

  // Close when the mouse leaves the panel after having been inside it.
  // Backdrop moves bubbled from the panel are ignored (e.target check), and
  // a pointer that never reached the panel (edge cards) won't trigger a close
  // while traveling toward it — those users close via click/Esc/✕.
  const maybeClose = () => {
    if (Date.now() - openedAt.current < 500) return;
    if (!enteredPanel.current) return;
    closeExpand();
  };
  const onBackdropMove = (e) => {
    if (e.target === e.currentTarget) maybeClose();
  };

  const onCardClick = (e) => {
    if (!fine) { e.preventDefault(); openExpand(); } // touch: first tap expands
  };

  const liveClass =
    "browser-live" +
    (loaded && !mFailed ? " is-ready" : "") +
    (mounted && !loaded && !mFailed ? " is-loading" : "");

  return (
    <>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={"browser-card" + (arming ? " is-arming" : "")}
        style={{ textDecoration: "none" }}
        ref={cardRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeaveCard}
        onFocus={handleEnter}
        onBlur={handleLeaveCard}
        onClick={onCardClick}
      >
        {/* Browser chrome bar */}
        <div className="browser-bar">
          <div className="browser-nav-btns">
            <span className="browser-nav-btn"><i className="bi bi-chevron-left" /></span>
            <span className="browser-nav-btn"><i className="bi bi-chevron-right" /></span>
          </div>
          <div className="browser-url-bar">
            <i className="bi bi-lock-fill" />
            <span className="browser-url-text">{displayUrl}</span>
          </div>
          <div className="browser-dots">
            <span className="browser-dot-r" />
            <span className="browser-dot-y" />
            <span className="browser-dot-g" />
          </div>
        </div>

        {/* Preview */}
        <div className="browser-preview">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={title}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentElement.classList.add("no-img");
              }}
            />
          ) : (
            <div className="browser-preview-placeholder">
              <div className="browser-preview-icon"><i className="bi bi-building" /></div>
            </div>
          )}

          {mounted && !mFailed && (
            <div className={liveClass}>
              <iframe
                src={url}
                title={title}
                loading="lazy"
                tabIndex={-1}
                aria-hidden="true"
                sandbox="allow-scripts allow-same-origin"
                referrerPolicy="no-referrer"
                onLoad={() => {
                  if (loadTimer.current) clearTimeout(loadTimer.current);
                  setLoaded(true);
                }}
                onError={() => {
                  if (loadTimer.current) clearTimeout(loadTimer.current);
                  setMFailed(true);
                }}
              />
              <div className="browser-live-loading">
                <span className="browser-live-spinner" />
              </div>
            </div>
          )}

          <div className="browser-preview-overlay" />
        </div>

        {/* Footer */}
        <div className="browser-info">
          <h3 className="browser-info-title">{title}</h3>
          <p className="browser-info-desc">{desc}</p>
          <span className="browser-info-link">
            {ctaLabel}
            <i className={`bi bi-arrow-up-${isRtl ? "left" : "right"}`} />
          </span>
        </div>
      </a>

      {/* ── EXPANDED FULL CARD (portal above the whole page) ── */}
      {expanded && createPortal(
        <div
          className="pexp-backdrop"
          ref={backdropRef}
          onClick={closeExpand}
          onMouseMove={onBackdropMove}
        >
          <div
            className="pexp-panel"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            ref={panelRef}
            dir={isRtl ? "rtl" : "ltr"}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => { enteredPanel.current = true; }}
            onMouseLeave={maybeClose}
          >
            <div className="pexp-bar">
              <span className="pexp-dots">
                <i className="pexp-dot-r" /><i className="pexp-dot-y" /><i className="pexp-dot-g" />
              </span>
              <a
                className="pexp-url"
                dir="ltr"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="bi bi-lock-fill" /> {displayUrl}
              </a>
              <span className="pexp-chip" aria-hidden="true">
                {pState === "loading" && <span className="pexp-spin" />}
                {pState === "live" && <span className="pexp-live-dot" />}
              </span>
              <button type="button" className="pexp-close" aria-label="Fermer" onClick={closeExpand}>
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className="pexp-body">
              {imgSrc ? (
                <img className="pexp-shot" src={imgSrc} alt="" aria-hidden="true" />
              ) : (
                <div className="pexp-shot-fallback"><i className="bi bi-building" /></div>
              )}
              {pState !== "failed" && (
                <iframe
                  className={`pexp-frame${pState === "live" ? " is-ready" : ""}`}
                  src={url}
                  title={title}
                  sandbox="allow-scripts allow-same-origin allow-popups"
                  referrerPolicy="no-referrer"
                  onLoad={() => {
                    if (pLoadTimer.current) clearTimeout(pLoadTimer.current);
                    setPState("live");
                  }}
                  onError={() => {
                    if (pLoadTimer.current) clearTimeout(pLoadTimer.current);
                    setPState("failed");
                  }}
                />
              )}
            </div>

            <div className="pexp-foot">
              <div className="pexp-meta">
                <h3 className="pexp-title">{title}</h3>
                <p className="pexp-desc">{desc}</p>
              </div>
              <a className="pexp-visit" href={url} target="_blank" rel="noopener noreferrer">
                {ctaLabel} <i className={`bi bi-arrow-up-${isRtl ? "left" : "right"}`} />
              </a>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM — "Comptoir"

   Palette   Built out from the existing CACI green and gold rather than
             replacing them. --leaf is the original brand green, untouched;
             --pine and --ink deepen it for large fields, and --brass is the
             gold pulled toward metal so it reads as a seal, not a highlight.
   Type      Bricolage Grotesque (display) over Instrument Sans (body), with
             JetBrains Mono for anything that is a record: dates, URLs, labels.
             IBM Plex Sans Arabic takes all three roles in Arabic.
   Signature The brass seal, and the guilloché engine-turn pattern behind the
             dark services band — both borrowed from the security print on the
             certificates this institution actually issues.
   ═══════════════════════════════════════════════════════════════════════════ */

const GLOBAL_STYLES = `
.caci-home {
  /* ── Colour ─────────────────────────────────────────────── */
  --ink:      #08150E;
  --ink-2:    #12281C;
  --pine:     #0F4A30;
  --leaf:     #1A7A4A;
  --leaf-2:   #22A060;
  --brass:    #B08A2E;
  --brass-2:  #D8B455;
  --paper:    #FAFBFA;
  --mint:     #E9F3ED;
  --surface:  #FFFFFF;
  --txt:      #0B1F16;
  --muted:    #5C7268;
  --rule:     rgba(15,74,48,.13);
  --rule-2:   rgba(15,74,48,.26);

  /* ── Type ───────────────────────────────────────────────── */
  --display: 'Bricolage Grotesque', 'IBM Plex Sans Arabic', system-ui, sans-serif;
  --body:    'Instrument Sans', 'IBM Plex Sans Arabic', system-ui, sans-serif;
  --mono:    'JetBrains Mono', ui-monospace, 'SFMono-Regular', monospace;

  /* ── Motion: one scale for the whole page ───────────────── */
  --e:    cubic-bezier(.22,1,.36,1);
  --e-io: cubic-bezier(.65,.05,.36,1);
  --t1:   .26s;   /* micro-interaction  */
  --t2:   .62s;   /* reveal            */
  --t3:   .95s;   /* hero              */

  /* ── Rhythm ─────────────────────────────────────────────── */
  --pad: clamp(56px, 8.5vw, 116px);
  --gut: clamp(18px, 4.4vw, 40px);

  position: relative;
  font-family: var(--body);
  color: var(--txt);
  background: var(--paper);
  overflow-x: clip;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}
.caci-home *, .caci-home *::before, .caci-home *::after { box-sizing: border-box; }
.caci-home img { max-width: 100%; }

/* Arabic gets one family across all three roles — Bricolage has no Arabic. */
.caci-home.is-rtl {
  --display: 'IBM Plex Sans Arabic', 'Bricolage Grotesque', system-ui, sans-serif;
  --body:    'IBM Plex Sans Arabic', 'Instrument Sans', system-ui, sans-serif;
}

.caci-shell {
  width: 100%; max-width: 1280px;
  margin-inline: auto;
  padding-inline: var(--gut);
}

/* ── Reading progress ─────────────────────────────────────────────────── */
.caci-progress {
  position: fixed; inset-block-start: 0; inset-inline: 0;
  height: 2px; z-index: 1200; pointer-events: none;
  background: linear-gradient(90deg, var(--leaf), var(--brass-2));
  transform-origin: left center;
  transition: transform .12s linear, opacity .3s ease;
}
.caci-home.is-rtl .caci-progress { transform-origin: right center; }

/* ── Reveal on scroll ─────────────────────────────────────────────────────
   Everything opts in with data-rv and staggers with --i. Reduced motion and
   the no-JS path both fall through to fully visible. */
.caci-home [data-rv] {
  opacity: 0;
  transition:
    opacity var(--t2) var(--e),
    transform var(--t2) var(--e),
    filter var(--t2) var(--e);
  transition-delay: calc(var(--i, 0) * 65ms);
  will-change: opacity, transform;
}
.caci-home [data-rv="rise"]  { transform: translateY(26px); }
.caci-home [data-rv="fade"]  { transform: none; }
.caci-home [data-rv="left"]  { transform: translateX(-32px); }
.caci-home [data-rv="right"] { transform: translateX(32px); }
.caci-home [data-rv="zoom"]  { transform: scale(.94); filter: blur(6px); }
.caci-home [data-rv].rv-in {
  opacity: 1; transform: none; filter: none;
  will-change: auto;
}

/* ── The seal ─────────────────────────────────────────────────────────────
   Presses in like a stamp hitting paper: oversized, rotated, then settled. */
.caci-home .seal { flex: 0 0 auto; display: block; }
.caci-home [data-rv] .seal,
.caci-home .seal[data-rv] {
  transform: scale(1.9) rotate(-14deg);
  opacity: 0;
  transition: transform .7s var(--e), opacity .4s ease;
}
.caci-home [data-rv].rv-in .seal { transform: none; opacity: 1; }
@keyframes sealSpin { to { transform: rotate(360deg); } }
.caci-home .seal-spin { animation: sealSpin 14s linear infinite; }

/* ── Section header ───────────────────────────────────────────────────── */
.caci-home .sec-head {
  display: grid; gap: 18px;
  margin-bottom: clamp(30px, 4.5vw, 54px);
}
.caci-home .sec-head-text { min-width: 0; }
.caci-home .sec-label {
  display: inline-flex; align-items: center; gap: 9px;
  font-family: var(--mono);
  font-size: 11px; font-weight: 600; letter-spacing: .18em;
  text-transform: uppercase; color: var(--brass);
  margin-bottom: 14px;
}
.caci-home .sec-label-seal { color: var(--brass); opacity: .85; }
.caci-home .sec-title {
  font-family: var(--display);
  font-size: clamp(1.85rem, 6.4vw, 3.25rem);
  font-weight: 700; letter-spacing: -.032em; line-height: 1.03;
  color: var(--ink); margin: 0; text-wrap: balance;
}
.caci-home .sec-sub {
  margin: 14px 0 0; max-width: 56ch;
  font-size: clamp(14.5px, 1.5vw, 16px); line-height: 1.72; color: var(--muted);
}
.caci-home .sec-head--dark .sec-title { color: #fff; }
.caci-home .sec-head--dark .sec-sub   { color: rgba(255,255,255,.62); }
.caci-home .sec-head--dark .sec-label { color: var(--brass-2); }

/* "See more" — a real row on a phone, a quiet link on a desktop. */
.caci-home .sec-more {
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  min-height: 48px; padding: 0 24px;
  border: 1px solid var(--rule-2); border-radius: 999px;
  font-family: var(--mono); font-size: 12px; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase;
  color: var(--pine); text-decoration: none; background: transparent;
  transition: background var(--t1) var(--e), color var(--t1) var(--e),
              border-color var(--t1) var(--e), transform var(--t1) var(--e);
}
.caci-home .sec-more i { transition: transform var(--t1) var(--e); }
.caci-home .sec-more:hover {
  background: var(--pine); border-color: var(--pine); color: #fff;
  transform: translateY(-2px);
}
.caci-home .sec-more:hover i { transform: translateX(4px); }
.caci-home.is-rtl .sec-more:hover i { transform: translateX(-4px); }
.caci-home .sec-head--dark .sec-more {
  border-color: rgba(216,180,85,.4); color: var(--brass-2);
}
.caci-home .sec-head--dark .sec-more:hover {
  background: var(--brass-2); border-color: var(--brass-2); color: var(--ink);
}
@media (max-width: 767.98px) {
  .caci-home .sec-more { width: 100%; }
}
@media (min-width: 768px) {
  .caci-home .sec-head {
    grid-template-columns: 1fr auto;
    align-items: end; gap: 32px;
  }
  .caci-home .sec-head-action { justify-self: end; }
}

/* ── Rail: swipeable on phones, a plain grid from 992px ───────────────── */
.caci-home .rail-shell { position: relative; }
.caci-home .rail {
  display: flex; gap: 14px;
  overflow-x: auto; overflow-y: hidden;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-block: 4px 6px;
  /* bleed to the screen edge so cards feel like they continue off-screen */
  margin-inline: calc(var(--gut) * -1);
  padding-inline: var(--gut);
  scroll-padding-inline: var(--gut);
}
.caci-home .rail::-webkit-scrollbar { display: none; }
.caci-home .rail > * {
  flex: 0 0 78%; max-width: 340px; min-width: 0; scroll-snap-align: start;
}
.caci-home .rail-bar {
  position: relative; height: 3px; border-radius: 3px;
  background: var(--rule); margin: 20px auto 0; width: 120px;
  opacity: 0; transition: opacity var(--t1) ease;
}
.caci-home .rail-bar.is-on { opacity: 1; }
.caci-home .rail-bar > span {
  position: absolute; inset-block: 0; width: 38%;
  border-radius: 3px; background: var(--pine);
  transition: inset-inline-start .18s var(--e);
}
@media (min-width: 576px) { .caci-home .rail > * { flex-basis: 54%; } }
@media (min-width: 768px) { .caci-home .rail > * { flex-basis: 40%; } }
@media (min-width: 992px) {
  .caci-home .rail {
    display: grid; grid-template-columns: repeat(var(--cols, 3), minmax(0, 1fr));
    gap: 24px; overflow: visible;
    margin-inline: 0; padding-inline: 0;
  }
  .caci-home .rail > * { max-width: none; }
  .caci-home .rail-bar { display: none; }
}

/* ═══════════════════════════════════════════════════════════════════════
   HERO
   ═══════════════════════════════════════════════════════════════════════ */
.caci-home .caci-hero {
  position: relative; width: 100%;
  height: 100vh; height: 100svh;
  min-height: 560px;
  overflow: hidden; isolation: isolate;
  background: var(--ink);
  touch-action: pan-y;
}
@media (max-width: 767.98px) {
  .caci-home .caci-hero {
    height: 88vh; height: 88svh;
    min-height: 480px; max-height: 780px;
  }
}

.caci-home .hero-slide {
  position: absolute; inset: 0;
  opacity: 0; visibility: hidden;
  transition: opacity var(--t3) var(--e-io), visibility var(--t3);
}
.caci-home .hero-slide.is-active { opacity: 1; visibility: visible; }

.caci-home .hero-img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover; object-position: center;
  transform: scale(1.13);
}
/* Slow cinematic drift, alternating direction so a loop never feels mechanical */
.caci-home .hero-slide.is-active .hero-img { animation: heroDriftA 9s linear both; }
.caci-home .hero-slide:nth-child(even).is-active .hero-img { animation-name: heroDriftB; }
@keyframes heroDriftA {
  from { transform: scale(1.14) translate3d(1.2%, 0.8%, 0); }
  to   { transform: scale(1.00) translate3d(0, 0, 0); }
}
@keyframes heroDriftB {
  from { transform: scale(1.00) translate3d(0, 0, 0); }
  to   { transform: scale(1.13) translate3d(-1.4%, -1%, 0); }
}

.caci-home .hero-scrim {
  position: absolute; inset: 0; z-index: 1;
  background:
    linear-gradient(to top,
      rgba(5,15,10,.93) 0%,
      rgba(5,15,10,.72) 26%,
      rgba(5,15,10,.24) 56%,
      rgba(5,15,10,.42) 100%);
}
@media (min-width: 992px) {
  .caci-home .hero-scrim {
    background:
      linear-gradient(100deg,
        rgba(5,15,10,.94) 0%,
        rgba(5,15,10,.78) 32%,
        rgba(5,15,10,.24) 66%,
        rgba(5,15,10,.05) 100%),
      linear-gradient(to top, rgba(5,15,10,.62), transparent 42%);
  }
  .caci-home.is-rtl .hero-scrim {
    background:
      linear-gradient(-100deg,
        rgba(5,15,10,.94) 0%,
        rgba(5,15,10,.78) 32%,
        rgba(5,15,10,.24) 66%,
        rgba(5,15,10,.05) 100%),
      linear-gradient(to top, rgba(5,15,10,.62), transparent 42%);
  }
}

.caci-home .hero-caption {
  position: absolute; inset: 0; z-index: 2;
  display: flex; align-items: flex-end;
  padding-bottom: clamp(92px, 15vh, 150px);
  pointer-events: none;
}
.caci-home .hero-caption > * { pointer-events: auto; }
@media (min-width: 992px) {
  .caci-home .hero-caption { align-items: center; padding-bottom: 0; }
}
.caci-home .hero-box { max-width: 720px; text-align: start; }
/* dir="auto" on the title/text can flip their intrinsic direction; pin the
   alignment to the page so the left (or right) edge of the column stays true. */
.caci-home.is-rtl .hero-title,
.caci-home.is-rtl .hero-text { text-align: right; }

.caci-home .hero-eyebrow {
  display: inline-flex; align-items: center; gap: 10px;
  font-family: var(--mono); font-size: 11px; font-weight: 600;
  letter-spacing: .26em; text-transform: uppercase;
  color: var(--brass-2); margin-bottom: 18px;
}
.caci-home .hero-eyebrow::after {
  content: ''; display: block; width: clamp(28px, 8vw, 64px); height: 1px;
  background: linear-gradient(to right, var(--brass-2), transparent);
}
.caci-home.is-rtl .hero-eyebrow::after {
  background: linear-gradient(to left, var(--brass-2), transparent);
}
.caci-home .hero-slide.is-active .hero-eyebrow {
  animation: rise .7s var(--e) both;
}

.caci-home .hero-title {
  font-family: var(--display);
  font-size: clamp(2.1rem, 8.2vw, 4.5rem);
  font-weight: 700; letter-spacing: -.035em; line-height: 1.02;
  color: #fff; margin: 0 0 18px;
  text-shadow: 0 2px 30px rgba(0,0,0,.35);
  text-wrap: balance;
}
.caci-home .hero-title .w {
  display: inline-block; opacity: 0;
  transform: translateY(.45em) rotate(2deg);
  filter: blur(8px);
}
.caci-home .hero-slide.is-active .hero-title .w {
  animation: wordIn .8s var(--e) both;
  animation-delay: calc(var(--i) * 62ms + 150ms);
}
@keyframes wordIn {
  to { opacity: 1; transform: none; filter: blur(0); }
}
@keyframes rise {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: none; }
}

.caci-home .hero-text {
  color: rgba(255,255,255,.82);
  font-size: clamp(14.5px, 1.7vw, 17px); line-height: 1.75;
  max-width: 54ch; margin: 0 0 28px;
}
.caci-home .hero-slide.is-active .hero-text {
  animation: rise .8s var(--e) both; animation-delay: .34s;
}

.caci-home .hero-cta {
  position: relative; overflow: hidden;
  display: inline-flex; align-items: center; justify-content: center; gap: 11px;
  min-height: 54px; padding: 0 30px; border-radius: 999px;
  background: linear-gradient(180deg, var(--leaf-2), var(--leaf));
  color: #fff; text-decoration: none;
  font-size: 15px; font-weight: 600; letter-spacing: .005em;
  box-shadow:
    0 14px 34px -12px rgba(26,122,74,.85),
    inset 0 0 0 1px rgba(255,255,255,.2),
    0 0 0 1px rgba(216,180,85,.35);
  transition: transform var(--t1) var(--e), box-shadow var(--t1) var(--e);
}
.caci-home .hero-slide.is-active .hero-cta {
  animation: rise .8s var(--e) both; animation-delay: .46s;
}
.caci-home .hero-cta::after {
  content: ''; position: absolute; inset-block: 0; inset-inline-start: -60%;
  width: 45%; transform: skewX(-18deg);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.32), transparent);
  transition: inset-inline-start .7s var(--e);
}
.caci-home .hero-cta:hover { color: #fff; transform: translateY(-3px); }
.caci-home .hero-cta:hover::after { inset-inline-start: 115%; }
.caci-home .hero-cta:active { transform: translateY(0) scale(.985); }
@media (max-width: 575.98px) {
  .caci-home .hero-cta { width: 100%; max-width: 420px; }
}

/* Indicators double as a timer: the active bar fills over the 5s dwell. */
.caci-home .hero-dots {
  position: absolute; inset-block-end: 50px;
  inset-inline: 0; z-index: 4;
  display: flex; gap: 9px; align-items: center;
  width: 100%; max-width: 1280px; margin-inline: auto;
  padding-inline: var(--gut);
}
.caci-home .hero-dot {
  position: relative; flex: 0 1 58px; min-width: 30px; height: 4px;
  padding: 0; border: 0; border-radius: 99px; cursor: pointer;
  background: rgba(255,255,255,.3);
  transition: background var(--t1) ease, flex-basis .5s var(--e);
}
.caci-home .hero-dot::after { content: ''; position: absolute; inset: -20px; }
.caci-home .hero-dot::before {
  content: ''; position: absolute; inset: 0;
  background: var(--brass-2); border-radius: 99px;
  transform: scaleX(0); transform-origin: left center;
}
.caci-home.is-rtl .hero-dot::before { transform-origin: right center; }
.caci-home .hero-dot.is-active { flex-basis: 92px; }
.caci-home .hero-dot.is-active::before { animation: dotFill 5s linear forwards; }
@keyframes dotFill { to { transform: scaleX(1); } }

.caci-home .hero-nav {
  position: absolute; inset-block-start: 50%; z-index: 4;
  transform: translateY(-50%);
  width: 54px; height: 54px; border-radius: 50%;
  display: none; align-items: center; justify-content: center;
  background: rgba(255,255,255,.09); color: #fff; cursor: pointer;
  border: 1px solid rgba(255,255,255,.22);
  -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
  transition: background var(--t1) var(--e), border-color var(--t1) var(--e),
              transform var(--t1) var(--e);
}
@media (min-width: 992px) { .caci-home .hero-nav { display: flex; } }
.caci-home .hero-nav:hover {
  background: var(--leaf); border-color: var(--leaf-2);
  transform: translateY(-50%) scale(1.06);
}
.caci-home .hero-nav.is-prev { inset-inline-start: 22px; }
.caci-home .hero-nav.is-next { inset-inline-end: 22px; }

/* Scroll cue — the seal, turning slowly. Desktop only; the phone has swipe. */
.caci-home .hero-cue {
  position: absolute; inset-block-end: 74px;
  inset-inline-end: var(--gut); z-index: 4;
  display: none; flex-direction: column; align-items: center; gap: 10px;
  color: rgba(255,255,255,.55); pointer-events: none;
}
@media (min-width: 992px) { .caci-home .hero-cue { display: flex; } }
.caci-home .hero-cue span {
  font-family: var(--mono); font-size: 9.5px;
  letter-spacing: .22em; text-transform: uppercase;
}
.caci-home .hero-cue i {
  display: block; width: 1px; height: 34px;
  background: linear-gradient(to bottom, rgba(255,255,255,.5), transparent);
  animation: cueDrop 2.4s var(--e-io) infinite;
}
@keyframes cueDrop {
  0%, 100% { opacity: .25; transform: scaleY(.4); transform-origin: top; }
  45%      { opacity: 1;   transform: scaleY(1);  transform-origin: top; }
}

/* The wave, kept but calmed down to a slow tide that hands off to the page. */
.caci-home .hero-waves {
  position: absolute; inset-inline: 0; inset-block-end: -1px;
  z-index: 3; height: 56px; pointer-events: none;
}
.caci-home .hero-waves svg { width: 100%; height: 100%; display: block; }
.caci-home .hero-waves use { fill: var(--paper); }
.caci-home .hero-waves .w1 { opacity: .32; animation: tide 22s ease-in-out infinite; }
.caci-home .hero-waves .w2 { opacity: .55; animation: tide 17s ease-in-out infinite reverse; }
.caci-home .hero-waves .w3 { opacity: 1; }
@keyframes tide {
  0%, 100% { transform: translateX(0); }
  50%      { transform: translateX(-14px); }
}
@media (max-width: 767.98px) {
  .caci-home .hero-waves { height: 30px; }
  .caci-home .hero-dots  { inset-block-end: 40px; }
}
@media (min-width: 992px) { .caci-home .hero-dots { inset-block-end: 74px; } }

/* ═══════════════════════════════════════════════════════════════════════
   ABOUT
   ═══════════════════════════════════════════════════════════════════════ */
.caci-home #caci-about { padding: var(--pad) 0; background: var(--paper); }
.caci-home .about-grid { display: grid; gap: 36px; }
@media (min-width: 992px) {
  .caci-home .about-grid {
    grid-template-columns: 1.06fr .94fr;
    gap: clamp(48px, 6vw, 92px);
  }
  .caci-home .about-col-2 {
    border-inline-start: 1px solid var(--rule);
    padding-inline-start: clamp(40px, 5vw, 76px);
  }
}
.caci-home .about-lead {
  font-size: clamp(16px, 2vw, 18.5px); line-height: 1.72;
  color: var(--txt); margin: 0;
}
.caci-home .about-lead::first-letter {
  font-family: var(--display); font-size: 3.1em; font-weight: 700;
  float: left; float: inline-start; line-height: .82; padding-inline-end: .1em;
  padding-block-start: .06em; color: var(--pine);
}
.caci-home.is-rtl .about-lead::first-letter { float: none; font-size: 1em; padding: 0; }
.caci-home .about-body {
  font-size: clamp(14.5px, 1.6vw, 16px); line-height: 1.8; color: var(--muted); margin: 0;
}
.caci-home .about-body p { margin: 0 0 1em; }
.caci-home .about-body p:last-child { margin-bottom: 0; }

/* Ledger-ruled checklist */
.caci-home .about-list { list-style: none; padding: 0; margin: 34px 0 0; }
.caci-home .about-list li {
  display: flex; align-items: center; gap: 14px;
  padding: 16px 0; border-block-end: 1px solid var(--rule);
  font-size: 15px; font-weight: 500; color: var(--txt);
}
.caci-home .about-list li:first-child { border-block-start: 1px solid var(--rule); }
.caci-home .about-check {
  flex: 0 0 auto; width: 30px; height: 30px; border-radius: 50%;
  background: var(--mint); color: var(--pine);
  display: grid; place-items: center; font-size: 14px;
  box-shadow: inset 0 0 0 1px var(--rule-2);
  transition: background var(--t1) var(--e), color var(--t1) var(--e),
              transform var(--t1) var(--e);
}
.caci-home .about-list li:hover .about-check {
  background: var(--pine); color: #fff; transform: rotate(-8deg) scale(1.06);
}

/* ═══════════════════════════════════════════════════════════════════════
   FEATURES — tabs. A vertical index on desktop, a swipeable chip row on
   phones. Panes cross-fade; the image wipes in from the leading edge.
   ═══════════════════════════════════════════════════════════════════════ */
.caci-home #caci-features { padding: var(--pad) 0; background: var(--surface); }
.caci-home .feat-layout { display: grid; gap: 28px; }
.caci-home .feat-panes { min-width: 0; }
@media (min-width: 992px) {
  .caci-home .feat-layout {
    grid-template-columns: 296px minmax(0, 1fr);
    gap: clamp(40px, 4.5vw, 72px); align-items: start;
  }
}

.caci-home .feat-tabs {
  display: flex; gap: 8px;
  overflow-x: auto; scroll-snap-type: x proximity;
  /* The strip is scrolled from JS when the active tab changes; without
     this the jump is instant on engines that ignore the behavior option. */
  scroll-behavior: smooth;
  scrollbar-width: none;
  margin-inline: calc(var(--gut) * -1); padding-inline: var(--gut);
  padding-block: 2px 10px;
  scroll-padding-inline: var(--gut);
}
.caci-home .feat-tabs::-webkit-scrollbar { display: none; }
.caci-home .feat-tab {
  position: relative; flex: 0 0 auto; scroll-snap-align: start;
  display: inline-flex; align-items: center; gap: 10px;
  min-height: 48px; padding: 0 18px;
  border: 1px solid var(--rule); border-radius: 999px;
  background: var(--paper); color: var(--muted); cursor: pointer;
  font-family: var(--body); font-size: 13.5px; font-weight: 600;
  text-align: start; white-space: nowrap;
  transition: background var(--t1) var(--e), color var(--t1) var(--e),
              border-color var(--t1) var(--e), transform var(--t1) var(--e);
}
.caci-home .feat-tab i { font-size: 17px; }
.caci-home .feat-tab:hover { color: var(--pine); border-color: var(--rule-2); }
.caci-home .feat-tab.is-active {
  background: var(--pine); border-color: var(--pine); color: #fff;
  box-shadow: 0 12px 26px -14px rgba(15,74,48,.9);
}
.caci-home .feat-tab:active { transform: scale(.97); }
@media (min-width: 992px) {
  .caci-home .feat-tabs {
    flex-direction: column; overflow: visible; gap: 4px;
    margin-inline: 0; padding-inline: 0;
    border-inline-start: 1px solid var(--rule);
  }
  .caci-home .feat-tab {
    width: 100%; border: 0; border-radius: 0 12px 12px 0;
    background: transparent; padding: 16px 18px; min-height: 56px;
    white-space: normal;
  }
  .caci-home.is-rtl .feat-tab { border-radius: 12px 0 0 12px; }
  .caci-home .feat-tab::before {
    content: ''; position: absolute; inset-block: 8px;
    inset-inline-start: -1px; width: 2px; border-radius: 2px;
    background: var(--brass);
    transform: scaleY(0); transform-origin: center;
    transition: transform .45s var(--e);
  }
  .caci-home .feat-tab.is-active {
    background: var(--paper); color: var(--ink); box-shadow: none;
  }
  .caci-home .feat-tab.is-active::before { transform: scaleY(1); }
}

.caci-home .feat-pane { display: none; }
.caci-home .feat-pane.is-active { display: block; }
.caci-home .feat-pane-inner { display: flex; flex-direction: column; gap: 22px; }
@media (min-width: 768px) {
  .caci-home .feat-pane-inner {
    display: grid; align-items: start;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: auto 1fr;
    grid-template-areas: "t m" "c m";
    gap: 0 clamp(28px, 4vw, 56px);
  }
  .caci-home .feat-title { grid-area: t; }
  .caci-home .feat-media { grid-area: m; align-self: center; }
  .caci-home .feat-copy  { grid-area: c; min-width: 0; }
}
.caci-home .feat-pane.is-active .feat-media {
  animation: mediaWipe .85s var(--e) both;
}
.caci-home .feat-pane.is-active .feat-title,
.caci-home .feat-pane.is-active .feat-copy > * {
  animation: rise .65s var(--e) both;
  animation-delay: calc(var(--i, 0) * 70ms + 120ms);
}
@keyframes mediaWipe {
  from { clip-path: inset(0 100% 0 0); transform: scale(1.05); }
  to   { clip-path: inset(0 0 0 0);    transform: scale(1); }
}
.caci-home.is-rtl .feat-pane.is-active .feat-media { animation-name: mediaWipeRtl; }
@keyframes mediaWipeRtl {
  from { clip-path: inset(0 0 0 100%); transform: scale(1.05); }
  to   { clip-path: inset(0 0 0 0);    transform: scale(1); }
}
.caci-home .feat-media {
  position: relative; border-radius: 20px; overflow: hidden;
  background: var(--mint); aspect-ratio: 4 / 3;
  box-shadow: 0 30px 70px -30px rgba(8,21,14,.5);
}
.caci-home .feat-media img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: transform 1.1s var(--e);
}
.caci-home .feat-media:hover img { transform: scale(1.05); }
.caci-home .feat-media::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.5), inset 0 -60px 70px -50px rgba(8,21,14,.55);
}
.caci-home .feat-title {
  font-family: var(--display);
  font-size: clamp(1.35rem, 3vw, 2.05rem); font-weight: 700;
  letter-spacing: -.028em; line-height: 1.12;
  color: var(--ink); margin: 0 0 14px;
}
.caci-home .feat-desc {
  font-size: 15px; line-height: 1.78; color: var(--muted); margin: 0 0 24px;
}
.caci-home .feat-desc p { margin: 0 0 .9em; }
.caci-home .feat-points { list-style: none; padding: 0; margin: 0 0 24px; display: grid; gap: 10px; }
.caci-home .feat-points li {
  display: flex; gap: 10px; align-items: flex-start;
  font-size: 14.5px; color: var(--muted);
}
.caci-home .feat-points i { color: var(--leaf); margin-top: 3px; }
.caci-home .feat-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  min-height: 50px; padding: 0 28px; border-radius: 999px;
  background: var(--pine); color: #fff; text-decoration: none;
  font-size: 14.5px; font-weight: 600;
  box-shadow: 0 14px 30px -16px rgba(15,74,48,.95);
  transition: transform var(--t1) var(--e), background var(--t1) var(--e);
}
.caci-home .feat-btn i { transition: transform var(--t1) var(--e); }
.caci-home .feat-btn:hover { background: var(--leaf); color: #fff; transform: translateY(-2px); }
.caci-home .feat-btn:hover i { transform: translateX(4px); }
.caci-home.is-rtl .feat-btn:hover i { transform: translateX(-4px); }
.caci-home .feat-btn:active { transform: translateY(0) scale(.98); }

/* ═══════════════════════════════════════════════════════════════════════
   SERVICES — the dark band. The engine-turn pattern behind it is the same
   guilloché used on the certificates this chamber issues.
   ═══════════════════════════════════════════════════════════════════════ */
.caci-home #caci-services {
  position: relative; overflow: hidden;
  padding: var(--pad) 0; background: var(--ink); color: #fff;
}
.caci-home #caci-services::before {
  content: ''; position: absolute; inset: 0; pointer-events: none; z-index: 0;
  background:
    repeating-linear-gradient(114deg, rgba(216,180,85,.075) 0 1px, transparent 1px 9px),
    repeating-linear-gradient(-114deg, rgba(34,160,96,.07) 0 1px, transparent 1px 9px);
  -webkit-mask-image: radial-gradient(125% 88% at 50% 0%, #000 18%, transparent 76%);
          mask-image: radial-gradient(125% 88% at 50% 0%, #000 18%, transparent 76%);
}
.caci-home #caci-services > * { position: relative; z-index: 1; }

.caci-home .svc-card {
  position: relative; display: flex; flex-direction: column;
  height: 100%; min-height: 340px;
  border-radius: 18px; overflow: hidden; text-decoration: none;
  background: var(--ink-2); border: 1px solid rgba(255,255,255,.08);
  transition: transform var(--t2) var(--e), border-color var(--t1) var(--e),
              box-shadow var(--t2) var(--e);
}
.caci-home .svc-card:hover {
  transform: translateY(-8px);
  border-color: rgba(216,180,85,.42);
  box-shadow: 0 40px 70px -34px rgba(0,0,0,.9);
}
.caci-home .svc-card:active { transform: translateY(-2px) scale(.995); }
.caci-home .svc-media { position: relative; height: 168px; flex: 0 0 auto; overflow: hidden; }
.caci-home .svc-media img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: transform 1s var(--e);
}
.caci-home .svc-card:hover .svc-media img { transform: scale(1.07); }
.caci-home .svc-media::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(to top, var(--ink-2) 4%, rgba(18,40,28,.15) 55%, transparent);
}
.caci-home .svc-icon {
  position: absolute; z-index: 2; inset-block-end: 14px; inset-inline-start: 18px;
  width: 42px; height: 42px; border-radius: 12px;
  display: grid; place-items: center; font-size: 19px;
  background: linear-gradient(180deg, var(--brass-2), var(--brass));
  color: var(--ink);
  box-shadow: 0 10px 24px -10px rgba(176,138,46,.9);
  transition: transform var(--t1) var(--e);
}
.caci-home .svc-card:hover .svc-icon { transform: translateY(-3px) rotate(-6deg); }
.caci-home .svc-body {
  flex: 1; display: flex; flex-direction: column;
  padding: 18px 22px 22px; min-width: 0;
}
.caci-home .svc-title {
  font-family: var(--display); font-size: 17px; font-weight: 700;
  letter-spacing: -.018em; line-height: 1.24; color: #fff; margin: 0 0 8px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.caci-home .svc-desc {
  flex: 1; font-size: 13.5px; line-height: 1.7; margin: 0 0 16px;
  color: rgba(255,255,255,.55);
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.caci-home .svc-link {
  display: inline-flex; align-items: center; gap: 7px; margin-top: auto;
  font-family: var(--mono); font-size: 11px; font-weight: 600;
  letter-spacing: .13em; text-transform: uppercase; color: var(--brass-2);
}
.caci-home .svc-link i { transition: transform var(--t1) var(--e); }
.caci-home .svc-card:hover .svc-link i { transform: translateX(5px); }
.caci-home.is-rtl .svc-card:hover .svc-link i { transform: translateX(-5px); }

/* ═══════════════════════════════════════════════════════════════════════
   PARTNERS
   ═══════════════════════════════════════════════════════════════════════ */
.caci-home #caci-partners {
  position: relative; overflow: hidden;
  padding: var(--pad) 0; background: var(--paper);
}
.caci-home #caci-partners::before {
  content: ''; position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background-image: radial-gradient(rgba(15,74,48,.09) 1px, transparent 1px);
  background-size: 22px 22px;
  -webkit-mask-image: radial-gradient(ellipse 78% 66% at 50% 38%, #000 26%, transparent 74%);
          mask-image: radial-gradient(ellipse 78% 66% at 50% 38%, #000 26%, transparent 74%);
}
.caci-home #caci-partners > * { position: relative; z-index: 1; }
.caci-home #caci-partners .swiper {
  padding-block: 8px 12px;
  margin-inline: calc(var(--gut) * -1); padding-inline: var(--gut);
  overflow: hidden;
}
.caci-home #caci-partners .swiper-wrapper { align-items: stretch; }
.caci-home #caci-partners .swiper-slide { height: auto; display: flex; }

.caci-home .browser-card {
  position: relative; display: flex; flex-direction: column; width: 100%;
  height: 372px; border-radius: 18px; overflow: hidden;
  text-decoration: none; color: inherit;
  background: var(--surface); border: 1px solid var(--rule);
  box-shadow: 0 4px 20px -10px rgba(8,21,14,.28);
  transition: transform var(--t2) var(--e), box-shadow var(--t2) var(--e),
              border-color var(--t1) var(--e);
}
.caci-home .browser-card:hover {
  transform: translateY(-8px);
  border-color: rgba(176,138,46,.45);
  box-shadow: 0 38px 70px -34px rgba(15,74,48,.5);
}
.caci-home .browser-card:active { transform: translateY(-2px) scale(.995); }
.caci-home .browser-bar {
  position: relative; flex: 0 0 auto;
  display: flex; align-items: center; gap: 10px;
  padding: 10px 13px; background: var(--paper);
  border-block-end: 1px solid var(--rule);
}
.caci-home .browser-dots { display: flex; gap: 5px; }
.caci-home .browser-dot-r,
.caci-home .browser-dot-y,
.caci-home .browser-dot-g {
  width: 9px; height: 9px; border-radius: 50%; display: block;
}
.caci-home .browser-dot-r { background: #E36B5C; }
.caci-home .browser-dot-y { background: var(--brass-2); }
.caci-home .browser-dot-g { background: var(--leaf-2); }
.caci-home .browser-url-bar {
  flex: 1; min-width: 0; height: 28px; border-radius: 999px;
  display: flex; align-items: center; gap: 6px; padding: 0 12px;
  background: var(--surface); border: 1px solid var(--rule);
  font-family: var(--mono); font-size: 10.5px; color: var(--muted);
  overflow: hidden; white-space: nowrap;
}
.caci-home .browser-url-bar i { color: var(--leaf); font-size: 10px; flex: 0 0 auto; }
.caci-home .browser-url-text { overflow: hidden; text-overflow: ellipsis; }
.caci-home .browser-nav-btns { display: flex; gap: 3px; }
.caci-home .browser-nav-btn {
  width: 20px; height: 20px; border-radius: 6px;
  display: grid; place-items: center;
  background: var(--mint); color: var(--muted); font-size: 10px;
}
.caci-home .browser-preview { position: relative; flex: 1; overflow: hidden; background: var(--mint); }
.caci-home .browser-preview img {
  width: 100%; height: 100%; object-fit: cover; object-position: top center;
  display: block; transition: transform 1s var(--e);
}
.caci-home .browser-card:hover .browser-preview img { transform: scale(1.05); }
.caci-home .browser-preview-placeholder {
  width: 100%; height: 100%; display: grid; place-items: center;
  background: linear-gradient(140deg, var(--mint), var(--paper));
}
.caci-home .browser-preview-icon {
  width: 54px; height: 54px; border-radius: 16px; display: grid; place-items: center;
  background: var(--pine); color: #fff; font-size: 24px;
  box-shadow: 0 14px 30px -14px rgba(15,74,48,.9);
}
.caci-home .browser-preview-overlay {
  position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(to top, rgba(8,21,14,.4), transparent 55%);
}
.caci-home .browser-live { position: absolute; inset: 0; opacity: 0; transition: opacity .5s ease; }
.caci-home .browser-live.is-ready { opacity: 1; }
.caci-home .browser-live iframe {
  position: absolute; top: 0; inset-inline-start: 0;
  width: 250%; height: 250%; transform: scale(.4); transform-origin: top left;
  border: 0; background: #fff; pointer-events: none;
}
.caci-home.is-rtl .browser-live iframe { transform-origin: top right; }
.caci-home .browser-live-loading {
  position: absolute; inset: 0; display: grid; place-items: center;
  background: rgba(250,251,250,.7); opacity: 0; transition: opacity .3s ease;
}
.caci-home .browser-live.is-loading .browser-live-loading { opacity: 1; }
.caci-home .browser-live-spinner {
  width: 22px; height: 22px; border-radius: 50%;
  border: 2px solid var(--rule); border-top-color: var(--leaf);
  animation: sealSpin .8s linear infinite;
}
.caci-home .browser-info {
  flex: 0 0 auto; padding: 14px 18px 17px;
  border-block-start: 1px solid var(--rule); background: var(--surface);
}
.caci-home .browser-info-title {
  font-family: var(--display); font-size: 15px; font-weight: 700;
  letter-spacing: -.015em; color: var(--ink); margin: 0 0 4px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.caci-home .browser-info-desc {
  font-size: 12.5px; line-height: 1.5; color: var(--muted); margin: 0 0 10px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.caci-home .browser-info-link {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: var(--mono); font-size: 10.5px; font-weight: 600;
  letter-spacing: .12em; text-transform: uppercase; color: var(--pine);
}
.caci-home .browser-info-link i { transition: transform var(--t1) var(--e); }
.caci-home .browser-card:hover .browser-info-link i { transform: translate(3px, -3px); }
/* hold-to-expand progress */
.caci-home .browser-card.is-arming .browser-bar::after {
  content: ''; position: absolute; inset-inline: 0; inset-block-end: -1px;
  height: 2px; z-index: 2;
  background: linear-gradient(90deg, var(--brass), var(--leaf-2));
  transform: scaleX(0); transform-origin: left center;
  animation: armFill 3s linear forwards;
}
.caci-home.is-rtl .browser-card.is-arming .browser-bar::after { transform-origin: right center; }
@keyframes armFill { to { transform: scaleX(1); } }

/* Swiper controls */
.caci-home .swiper-button-prev,
.caci-home .swiper-button-next {
  width: 44px; height: 44px; border-radius: 50%;
  background: var(--surface); color: var(--pine);
  border: 1px solid var(--rule-2);
  box-shadow: 0 12px 26px -14px rgba(8,21,14,.5);
  transition: background var(--t1) var(--e), color var(--t1) var(--e),
              transform var(--t1) var(--e);
}
.caci-home .swiper-button-prev::after,
.caci-home .swiper-button-next::after { font-size: 14px; font-weight: 800; }
.caci-home .swiper-button-prev:hover,
.caci-home .swiper-button-next:hover {
  background: var(--pine); color: #fff; transform: scale(1.07);
}
@media (max-width: 767.98px) {
  .caci-home .swiper-button-prev,
  .caci-home .swiper-button-next { display: none; }
}

/* ═══════════════════════════════════════════════════════════════════════
   ARTICLES
   ═══════════════════════════════════════════════════════════════════════ */
.caci-home #caci-articles { padding: var(--pad) 0; background: var(--surface); }
.caci-home .art-card {
  position: relative;
  display: flex; flex-direction: column; height: 100%; min-height: 360px;
  border-radius: 18px; overflow: hidden;
  background: var(--surface); border: 1px solid var(--rule);
  box-shadow: 0 4px 18px -12px rgba(8,21,14,.3);
  transition: transform var(--t2) var(--e), box-shadow var(--t2) var(--e),
              border-color var(--t1) var(--e);
}
.caci-home .art-card:hover {
  transform: translateY(-8px);
  border-color: rgba(176,138,46,.4);
  box-shadow: 0 38px 64px -34px rgba(15,74,48,.45);
}
.caci-home .art-media { position: relative; height: 196px; flex: 0 0 auto; overflow: hidden; background: var(--mint); }
.caci-home .art-media img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: transform 1s var(--e);
}
.caci-home .art-card:hover .art-media img { transform: scale(1.06); }
.caci-home .art-date {
  position: absolute; z-index: 2; inset-block-end: 12px; inset-inline-start: 12px;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 999px;
  background: rgba(8,21,14,.62); color: #fff;
  -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
  font-family: var(--mono); font-size: 10.5px; letter-spacing: .04em;
}
.caci-home .art-date i { color: var(--brass-2); font-size: 11px; }
.caci-home .art-body { flex: 1; display: flex; flex-direction: column; padding: 16px 20px 20px; }
.caci-home .art-cats { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.caci-home .art-cat {
  display: inline-flex; align-items: center; min-height: 30px;
  padding: 4px 13px; border-radius: 999px;
  background: var(--mint); color: var(--pine); text-decoration: none;
  font-family: var(--mono); font-size: 10px; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase;
  transition: background var(--t1) var(--e), color var(--t1) var(--e);
}
.caci-home .art-cat:hover { background: var(--pine); color: #fff; }
.caci-home .art-title {
  font-family: var(--display); font-size: 16.5px; font-weight: 700;
  letter-spacing: -.02em; line-height: 1.34; color: var(--ink);
  text-decoration: none; margin: 0; display: block;
  transition: color var(--t1) var(--e);
}
/* The clamp lives on the span, not the link: overflow:hidden on the link
   itself would clip the stretched hit area below back to the text box. */
.caci-home .art-title > span {
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.caci-home .art-card:hover .art-title { color: var(--pine); }
/* The whole card opens the article, not just the 22px of title text. */
.caci-home .art-title::after { content: ''; position: absolute; inset: 0; z-index: 1; }
.caci-home .art-cat { position: relative; z-index: 2; }
.caci-home .art-foot {
  display: flex; align-items: center; gap: 8px; margin-top: auto; padding-top: 16px;
  font-family: var(--mono); font-size: 10.5px; font-weight: 600;
  letter-spacing: .12em; text-transform: uppercase; color: var(--leaf);
}
.caci-home .art-foot i { transition: transform var(--t1) var(--e); }
.caci-home .art-card:hover .art-foot i { transform: translateX(4px); }
.caci-home.is-rtl .art-card:hover .art-foot i { transform: translateX(-4px); }

/* ═══════════════════════════════════════════════════════════════════════
   CHAMBRES — the network. Each logo sits in a seal-like ring.
   ═══════════════════════════════════════════════════════════════════════ */
.caci-home #caci-chambres { padding: var(--pad) 0; background: var(--paper); }
.caci-home #caci-chambres .swiper {
  margin-inline: calc(var(--gut) * -1); padding-inline: var(--gut);
  padding-block: 6px 10px;
}
.caci-home #caci-chambres .swiper-slide { height: auto; }
.caci-home .chambre-card {
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  height: 100%; padding: 26px 14px; border-radius: 18px;
  background: var(--surface); border: 1px solid var(--rule);
  text-decoration: none; color: inherit;
  transition: transform var(--t2) var(--e), border-color var(--t1) var(--e),
              box-shadow var(--t2) var(--e);
}
.caci-home .chambre-card:hover {
  transform: translateY(-6px); border-color: rgba(176,138,46,.45);
  box-shadow: 0 30px 56px -32px rgba(15,74,48,.5);
}
.caci-home .chambre-ring {
  position: relative; width: 74px; height: 74px; border-radius: 50%;
  display: grid; place-items: center; overflow: hidden;
  background: var(--paper);
  box-shadow: inset 0 0 0 1px var(--rule);
  transition: box-shadow var(--t1) var(--e), transform var(--t2) var(--e);
}
.caci-home .chambre-card:hover .chambre-ring {
  box-shadow: inset 0 0 0 2px var(--brass); transform: rotate(-4deg) scale(1.04);
}
.caci-home .chambre-ring img { width: 100%; height: 100%; object-fit: cover; }
.caci-home .chambre-name {
  font-size: 12.5px; font-weight: 600; line-height: 1.35;
  text-align: center; color: var(--ink);
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}

/* ═══════════════════════════════════════════════════════════════════════
   POPUP — a bottom sheet on phones, a centred card above that.
   ═══════════════════════════════════════════════════════════════════════ */
.caci-pop-backdrop {
  position: fixed; inset: 0; z-index: 1100;
  display: flex; align-items: flex-end; justify-content: center;
  background: rgba(5,15,10,.6);
  -webkit-backdrop-filter: blur(7px); backdrop-filter: blur(7px);
  animation: popFade .35s ease both;
  padding: 0;
}
@keyframes popFade { from { opacity: 0; } to { opacity: 1; } }
.caci-pop {
  position: relative; width: 100%; max-width: 620px;
  max-height: 92vh; max-height: 92svh; overflow: auto;
  background: var(--surface);
  border-radius: 22px 22px 0 0;
  box-shadow: 0 -20px 70px rgba(5,15,10,.4);
  animation: sheetUp .5s cubic-bezier(.22,1,.36,1) both;
  font-family: 'Instrument Sans', system-ui, sans-serif;
}
@keyframes sheetUp { from { transform: translateY(100%); } to { transform: none; } }
.caci-pop-grip {
  width: 42px; height: 4px; border-radius: 4px;
  background: rgba(15,74,48,.2); margin: 10px auto 0;
}
.caci-pop-head {
  display: flex; align-items: center; gap: 14px;
  padding: 16px 20px; border-bottom: 1px solid rgba(15,74,48,.13);
}
.caci-pop-head h5 {
  flex: 1; margin: 0; font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  font-size: 17px; font-weight: 700; letter-spacing: -.02em; color: #08150E;
}
.caci-pop-x {
  flex: 0 0 auto; width: 40px; height: 40px; border-radius: 50%;
  border: 1px solid rgba(15,74,48,.13); background: #FAFBFA;
  color: #5C7268; font-size: 17px; cursor: pointer;
  display: grid; place-items: center;
  transition: background .26s cubic-bezier(.22,1,.36,1), color .26s, transform .26s;
}
.caci-pop-x:hover { background: #0F4A30; color: #fff; transform: rotate(90deg); }
.caci-pop-body img { width: 100%; display: block; }
.caci-pop-actions { display: grid; gap: 10px; padding: 16px 20px 22px; }
.caci-pop-primary, .caci-pop-secondary {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  min-height: 50px; padding: 0 24px; border-radius: 999px;
  font-size: 15px; font-weight: 600; cursor: pointer; text-decoration: none;
  transition: transform .26s cubic-bezier(.22,1,.36,1), background .26s;
}
.caci-pop-primary {
  background: linear-gradient(180deg, #22A060, #1A7A4A); color: #fff; border: 0;
  box-shadow: 0 14px 30px -14px rgba(26,122,74,.9);
}
.caci-pop-primary:hover { color: #fff; transform: translateY(-2px); }
.caci-pop-secondary {
  background: transparent; color: #5C7268; border: 1px solid rgba(15,74,48,.2);
}
.caci-pop-secondary:hover { background: #E9F3ED; color: #0F4A30; }
@media (min-width: 640px) {
  .caci-pop-backdrop { align-items: center; padding: 24px; }
  .caci-pop {
    border-radius: 24px; max-height: 88vh;
    animation-name: popIn;
    box-shadow: 0 40px 100px rgba(5,15,10,.45);
  }
  .caci-pop-grip { display: none; }
  @keyframes popIn {
    from { opacity: 0; transform: translateY(24px) scale(.96); }
    to   { opacity: 1; transform: none; }
  }
  .caci-pop-actions { grid-auto-flow: column; justify-content: flex-end; }
}

/* ═══════════════════════════════════════════════════════════════════════
   SECTION STATES — skeleton, empty, error

   There is no whole-page loader any more. Every section paints its own
   placeholder at the exact geometry of the content that will replace it, so
   nothing on the page moves when data lands. The skeletons deliberately
   reuse the real card metrics (heights, radii, gaps) rather than generic
   grey boxes — that is what makes the swap invisible.
   ═══════════════════════════════════════════════════════════════════════ */
.caci-home .skel {
  display: block; position: relative; overflow: hidden;
  background: var(--mint); border-radius: 8px;
  flex: 0 0 auto;
}
.caci-home .skel::after {
  content: ''; position: absolute; inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.75), transparent);
  animation: skelSweep 1.45s var(--e-io) infinite;
}
.caci-home.is-rtl .skel::after { animation-name: skelSweepRtl; }
@keyframes skelSweep    { to { transform: translateX(100%);  } }
@keyframes skelSweepRtl { to { transform: translateX(-200%); } }

/* On the dark band and over the hero the sweep has to run the other way
   round — a white shimmer on a white box is invisible. */
.caci-home #caci-services .skel,
.caci-home .hero-skel .skel {
  background: rgba(255,255,255,.07);
}
.caci-home #caci-services .skel::after,
.caci-home .hero-skel .skel::after {
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.16), transparent);
}

/* Skeleton shells mirror the real cards exactly. */
.caci-home .skel-card {
  display: flex; flex-direction: column; height: 100%;
  border-radius: 18px; overflow: hidden;
  border: 1px solid var(--rule); background: var(--surface);
}
.caci-home .skel-card > .skel { border-radius: 0; }
.caci-home .skel-card-body { padding: 16px 20px 20px; display: grid; gap: 10px; }
.caci-home #caci-services .skel-card {
  min-height: 340px; background: var(--ink-2); border-color: rgba(255,255,255,.08);
}
.caci-home #caci-services .skel-card .skel-media { height: 168px; }
.caci-home #caci-articles .skel-card { min-height: 360px; }
.caci-home #caci-articles .skel-card .skel-media { height: 196px; }
.caci-home #caci-partners .skel-card { height: 372px; }
.caci-home #caci-partners .skel-card .skel-bar {
  height: 48px; border-block-end: 1px solid var(--rule); background: var(--paper);
}
.caci-home #caci-partners .skel-card .skel-media { flex: 1; }

.caci-home .skel-chambre {
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  height: 100%; padding: 26px 14px; border-radius: 18px;
  background: var(--surface); border: 1px solid var(--rule);
}
.caci-home .skel-chambre .skel-ring { width: 74px; height: 74px; border-radius: 50%; }

/* Hero placeholder: sits in the same box the caption will occupy, so the
   headline does not jump when the first slide resolves. */
.caci-home .hero-skel {
  position: absolute; inset: 0; z-index: 2;
  display: flex; align-items: flex-end;
  padding-bottom: clamp(92px, 15vh, 150px);
}
@media (min-width: 992px) {
  .caci-home .hero-skel { align-items: center; padding-bottom: 0; }
}
.caci-home .hero-skel-box { max-width: 720px; display: grid; gap: 16px; }
.caci-home .hero-skel-box .skel-line { height: clamp(30px, 6vw, 52px); border-radius: 10px; }

/* Features placeholder keeps the two-column shape of the real pane. */
.caci-home .skel-tabs { display: flex; gap: 8px; flex-wrap: wrap; }
.caci-home .skel-tabs .skel { height: 48px; width: 128px; border-radius: 999px; }
@media (min-width: 992px) {
  .caci-home .skel-tabs { flex-direction: column; flex-wrap: nowrap; }
  .caci-home .skel-tabs .skel { width: 100%; height: 56px; border-radius: 10px; }
}
.caci-home .skel-pane { display: flex; flex-direction: column; gap: 22px; }
@media (min-width: 768px) {
  .caci-home .skel-pane {
    display: grid; align-items: start;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: auto 1fr;
    grid-template-areas: "t m" "c m";
    gap: 0 clamp(28px, 4vw, 56px);
  }
  .caci-home .skel-pane-title { grid-area: t; }
  .caci-home .skel-pane-media { grid-area: m; align-self: center; }
  .caci-home .skel-pane-copy  { grid-area: c; min-width: 0; }
}
.caci-home .skel-pane-title { height: 38px; width: min(70%, 340px); border-radius: 10px; }
.caci-home .skel-pane-media { aspect-ratio: 4 / 3; border-radius: 20px; }
.caci-home .skel-pane-copy { display: grid; gap: 12px; margin-block-start: 14px; }
.caci-home .skel-pane-copy .skel { height: 14px; border-radius: 7px; }
.caci-home .skel-pane-copy .skel:nth-child(2) { width: 88%; }
.caci-home .skel-pane-copy .skel:nth-child(3) { width: 62%; }
.caci-home .skel-pane-copy .skel:last-child {
  height: 50px; width: 172px; border-radius: 999px; margin-block-start: 8px;
}

/* ── Empty and failed states ────────────────────────────────────────────
   These are two different things and must not read the same. An empty
   section is simply quiet. A failed one says what happened and offers the
   one action that fixes it, so the visitor is not left refreshing the page. */
.caci-home .sec-note {
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  padding: 44px 24px; text-align: center;
  border: 1px dashed var(--rule-2); border-radius: 18px;
  background: rgba(15,74,48,.022);
  color: var(--muted); font-size: 14.5px; line-height: 1.6;
}
.caci-home .sec-note p { margin: 0; max-width: 42ch; }
.caci-home .sec-note .seal { color: var(--rule-2); }
.caci-home .sec-retry {
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  min-height: 46px; padding: 0 24px; border-radius: 999px; cursor: pointer;
  background: transparent; border: 1px solid var(--rule-2); color: var(--pine);
  font-family: var(--mono); font-size: 12px; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase;
  transition: background var(--t1) var(--e), color var(--t1) var(--e),
              transform var(--t1) var(--e);
}
.caci-home .sec-retry:hover { background: var(--pine); border-color: var(--pine); color: #fff; }
.caci-home .sec-retry:active { transform: scale(.97); }
.caci-home .sec-retry i { transition: transform var(--t1) var(--e); }
.caci-home .sec-retry:hover i { transform: rotate(-180deg); }

.caci-home #caci-services .sec-note {
  border-color: rgba(255,255,255,.16); background: rgba(255,255,255,.02);
  color: rgba(255,255,255,.6);
}
.caci-home #caci-services .sec-note .seal { color: rgba(216,180,85,.5); }
.caci-home #caci-services .sec-retry { border-color: rgba(216,180,85,.4); color: var(--brass-2); }
.caci-home #caci-services .sec-retry:hover {
  background: var(--brass-2); border-color: var(--brass-2); color: var(--ink);
}

/* The hero has no container to fall back on, so its note is centred in place. */
.caci-home .hero-note {
  position: absolute; inset: 0; z-index: 2;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 16px; padding: 32px; text-align: center; color: rgba(255,255,255,.72);
}
.caci-home .hero-note p { margin: 0; max-width: 40ch; font-size: 15px; line-height: 1.6; }
.caci-home .hero-note .seal { color: rgba(216,180,85,.55); }
.caci-home .hero-note .sec-retry { border-color: rgba(255,255,255,.3); color: #fff; }
.caci-home .hero-note .sec-retry:hover { background: #fff; border-color: #fff; color: var(--ink); }

/* ═══════════════════════════════════════════════════════════════════════
   ACCESSIBILITY & MOTION PREFERENCES
   ═══════════════════════════════════════════════════════════════════════ */
.caci-home a:focus-visible,
.caci-home button:focus-visible,
.caci-home [tabindex]:focus-visible {
  outline: 3px solid var(--brass); outline-offset: 3px; border-radius: 8px;
}
/* No hover work on touch screens — it only ever fires stuck states there. */
@media (hover: none) {
  .caci-home .hero-cta::after { display: none; }
  .caci-home .browser-card:hover,
  .caci-home .svc-card:hover,
  .caci-home .art-card:hover,
  .caci-home .chambre-card:hover { transform: none; box-shadow: inherit; }
}
@media (prefers-reduced-motion: reduce) {
  .caci-home *, .caci-home *::before, .caci-home *::after,
  .caci-pop, .caci-pop-backdrop, .pexp-panel, .pexp-backdrop {
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: .01ms !important;
    scroll-behavior: auto !important;
  }
  .caci-home [data-rv],
  .caci-home .hero-title .w,
  .caci-home .hero-eyebrow,
  .caci-home .hero-text,
  .caci-home .hero-cta { opacity: 1 !important; transform: none !important; filter: none !important; }
  .caci-home .hero-img { transform: none !important; }
  .caci-home .feat-media { clip-path: none !important; }
  .caci-home .skel::after { animation: none !important; }
}
`;

/* ═══════════════════════════════════════════════════════════════════════════
   PARTNER FLY-OUT PANEL
   Rendered through a portal onto <body>, so it sits outside .caci-home and
   cannot be scoped. Kept deliberately short and self-contained.
   ═══════════════════════════════════════════════════════════════════════════ */
const PORTAL_STYLES = `
.pexp-backdrop {
  position: fixed; inset: 0; z-index: 99990;
  background: rgba(5,15,10,.5);
  -webkit-backdrop-filter: blur(0px); backdrop-filter: blur(0px);
  opacity: 0; transition: opacity .45s ease, backdrop-filter .45s ease;
}
.pexp-backdrop.is-open {
  opacity: 1;
  -webkit-backdrop-filter: blur(9px) saturate(115%);
          backdrop-filter: blur(9px) saturate(115%);
}
.pexp-panel {
  position: fixed; z-index: 99991;
  display: flex; flex-direction: column; overflow: hidden;
  background: #fff; border-radius: 22px;
  font-family: 'Instrument Sans', system-ui, sans-serif;
  box-shadow:
    0 1px 0 rgba(255,255,255,.7) inset,
    0 70px 150px rgba(5,15,10,.5),
    0 0 0 1.5px rgba(176,138,46,.4);
  transition:
    top .58s cubic-bezier(.22,1,.36,1),
    left .58s cubic-bezier(.22,1,.36,1),
    width .58s cubic-bezier(.22,1,.36,1),
    height .58s cubic-bezier(.22,1,.36,1),
    border-radius .58s ease;
}
.pexp-bar {
  flex: 0 0 auto; display: flex; align-items: center; gap: 11px;
  height: 50px; padding: 0 14px;
  background: #FAFBFA; border-bottom: 1px solid rgba(15,74,48,.13);
}
.pexp-dots { display: flex; gap: 5px; }
.pexp-dots i { width: 9px; height: 9px; border-radius: 50%; display: block; }
.pexp-dot-r { background: #E36B5C; }
.pexp-dot-y { background: #D8B455; }
.pexp-dot-g { background: #22A060; }
.pexp-url {
  flex: 1; min-width: 0; display: inline-flex; align-items: center; gap: 7px;
  height: 32px; padding: 0 14px; border-radius: 999px;
  background: #fff; border: 1px solid rgba(15,74,48,.13);
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11.5px; color: #5C7268; text-decoration: none;
  overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
  transition: color .2s ease, border-color .2s ease;
}
.pexp-url i { color: #1A7A4A; font-size: 10px; flex: 0 0 auto; }
.pexp-url:hover { color: #0F4A30; border-color: rgba(176,138,46,.5); }
.pexp-chip { width: 20px; flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; }
.pexp-spin {
  width: 14px; height: 14px; border-radius: 50%; display: inline-block;
  border: 2px solid rgba(26,122,74,.25); border-top-color: #1A7A4A;
  animation: pexpSpin .7s linear infinite;
}
@keyframes pexpSpin { to { transform: rotate(360deg); } }
.pexp-live-dot {
  width: 9px; height: 9px; border-radius: 50%; background: #22A060;
  animation: pexpPulse 1.8s ease-out infinite;
}
@keyframes pexpPulse {
  0%   { box-shadow: 0 0 0 0 rgba(34,160,96,.5); }
  70%  { box-shadow: 0 0 0 8px rgba(34,160,96,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,160,96,0); }
}
.pexp-close {
  flex: 0 0 auto; width: 38px; height: 38px; border-radius: 50%;
  border: 1px solid rgba(15,74,48,.13); background: #E9F3ED; color: #0F4A30;
  display: grid; place-items: center; font-size: 14px; cursor: pointer;
  transition: background .25s ease, color .25s ease, transform .25s ease;
}
.pexp-close:hover { background: #0F4A30; color: #fff; transform: rotate(90deg); }
.pexp-body { position: relative; flex: 1; background: #E9F3ED; overflow: hidden; }
.pexp-shot { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: top center; }
.pexp-shot-fallback {
  position: absolute; inset: 0; display: grid; place-items: center;
  background: linear-gradient(140deg, #E9F3ED, #FAFBFA);
  font-size: 62px; color: #1A7A4A; opacity: .45;
}
.pexp-frame {
  position: absolute; top: 0; left: 0;
  width: 133.34%; height: 133.34%;
  transform: scale(.75); transform-origin: top left;
  border: 0; background: #fff;
  opacity: 0; transition: opacity .5s ease .1s; pointer-events: none;
}
.pexp-frame.is-ready { opacity: 1; pointer-events: auto; }
.pexp-foot {
  flex: 0 0 auto; display: flex; align-items: center; gap: 18px;
  padding: 14px 20px; background: #fff; border-top: 1px solid rgba(15,74,48,.13);
}
.pexp-meta { flex: 1; min-width: 0; }
.pexp-title {
  margin: 0 0 2px; font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  font-size: 17px; font-weight: 700; letter-spacing: -.02em; color: #08150E;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pexp-desc {
  margin: 0; font-size: 12.5px; color: #5C7268;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pexp-visit {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  min-height: 48px; padding: 0 26px; border-radius: 999px; text-decoration: none;
  background: linear-gradient(180deg, #22A060, #1A7A4A); color: #fff;
  font-size: 14.5px; font-weight: 600;
  box-shadow: 0 14px 30px -14px rgba(26,122,74,.9);
  transition: transform .25s ease;
}
.pexp-visit:hover { transform: translateY(-2px); color: #fff; }
@media (max-width: 767.98px) {
  .pexp-bar { height: 46px; padding: 0 10px; gap: 8px; }
  .pexp-dots { display: none; }
  .pexp-frame { width: 250%; height: 250%; transform: scale(.4); }
  .pexp-foot { flex-direction: column; align-items: stretch; gap: 10px; padding: 12px 14px; }
  .pexp-title, .pexp-desc { white-space: normal; }
  .pexp-desc { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .pexp-visit { width: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .pexp-panel, .pexp-backdrop, .pexp-frame { transition: none !important; }
}
`;

function Main() {
  const { t, i18n } = useTranslation();

  const [slides, setSlides] = useState([]);
  const [features, setFeatures] = useState([]);
  const [services, setServicess] = useState([]);
  const [homeArticle, setHomeArticles] = useState([]);
  const [popup, setPopups] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [partenaires, setPartenaires] = useState([]);
  const [chambres, setChambres] = useState([]);
  const [hasShownPopup, setHasShownPopup] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  // Autorotation of the features tabs. Two separate reasons to hold
  // still: the pointer is inside the block, or the tab is in the
  // background. One flag each, so leaving the window and coming back
  // cannot cancel a hover the visitor is still holding.
  const [featPaused, setFeatPaused] = useState(false);
  const [featHidden, setFeatHidden] = useState(false);

  // One status per section: "loading" | "ready" | "error". A section that
  // fails no longer takes the other six down with it.
  const [status, setStatus] = useState({
    slides: "loading", features: "loading", services: "loading",
    articles: "loading", popup: "loading", partenaires: "loading",
    chambres: "loading",
  });

  const partenairesSwiper = useRef(null);
  const chambresSwiper = useRef(null);
  const slideInterval = useRef(null);
  const featTabsRef = useRef(null);
  const featTouchTimer = useRef(null);
  const touchStart = useRef(null);

  const isRtl = i18n.language === "ar";
  const progress = useScrollProgress();

  // ─── Multilingual helpers ───────────────────────────────────────────────

  const getLocalizedField = (item, field, fieldAr, fieldEn) => {
    if (!item) return "";
    const lang = i18n.language;
    if (lang === "ar") return item[fieldAr] || item[field] || "";
    if (lang === "en") return item[fieldEn] || item[field] || "";
    return item[field] || "";
  };

  const getTitleslide = (slide) => getLocalizedField(slide, "title", "title_ar", "title_en");
  const getLinkTextslide = (slide) => getLocalizedField(slide, "link_text", "link_text_ar", "link_text_en");
  const getTitlefeature = (feature) => getLocalizedField(feature, "name", "name_ar", "name_en");

  const stripHtml = (raw) => {
    if (!raw) return "";
    const div = document.createElement("div");
    div.innerHTML = raw;
    return div.innerText || div.textContent || "";
  };

  const getDescriptionslide = (slide) => {
    if (!slide) return "";
    const raw = getLocalizedField(slide, "description", "description_ar", "description_en");
    if (!raw) return "";
    const text = stripHtml(raw);
    return text.split(" ").slice(0, 50).join(" ") + "...";
  };

  const getLocalizedImage = (item, field, fieldAr, fieldEn) => {
    if (!item) return null;
    const lang = i18n.language;
    let url;
    if (lang === "ar") url = item[fieldAr] || item[field];
    else if (lang === "en") url = item[fieldEn] || item[field];
    else url = item[field];
    return url ? `${BASE_URL}${url}` : null;
  };

  const getImageslide = (slide) => getLocalizedImage(slide, "image", "image_ar", "image_en");
  const getImagefeature = (feature) => getLocalizedImage(feature, "image", "image_ar", "image_en");
  const getImagehomeArticle = (article) => getLocalizedImage(article, "image", "image_ar", "image_en");

  const getShortDescriptionFeature = (feature) => {
    if (!feature) return "";
    const raw = getLocalizedField(feature, "description", "description_ar", "description_en");
    if (!raw) return "";
    return stripHtml(raw).split(" ").slice(0, 100).join(" ") + "...";
  };

  const getShortDescriptionService = (service) => {
    if (!service) return "";
    const raw = getLocalizedField(service, "description", "description_ar", "description_en");
    if (!raw) return "";
    return stripHtml(raw).split(" ").slice(0, 15).join(" ") + "...";
  };

  const decodeHtmlEntities = (text) => {
    if (!text) return "";
    let decoded = text;
    const textarea = document.createElement("textarea");
    let i = 0;
    do {
      textarea.innerHTML = decoded;
      decoded = textarea.value;
      i++;
    } while (decoded.includes("&") && i < 10);
    return decoded;
  };

  const getTitlehomeArticle = (item) => {
    if (!item) return "";
    const title = getLocalizedField(item, "name", "name_ar", "name_en");
    if (!title) return "";
    const decoded = decodeHtmlEntities(title);
    return decoded.length > 60 ? decoded.slice(0, 60) + "..." : decoded;
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("home.UnknownDate");
    const date = new Date(dateString);
    if (isNaN(date)) return t("home.InvalidDate");
    const locale = { en: "en-US", ar: "ar-AR", fr: "fr-FR" }[i18n.language] || "en-US";
    return date.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
  };

  const langPath = (path) => (i18n.language === "fr" ? path : `/${i18n.language}${path}`);

  // ─── Data fetching ──────────────────────────────────────────────────────
  // Seven independent requests instead of one Promise.all. The old version
  // resolved as a single unit, so the slowest endpoint set the time-to-first-
  // paint for the entire page and one rejection wiped all seven arrays.

  // StrictMode mounts every effect twice in development. The cleanup must be
  // paired with a matching set on mount, or the flag latches false after the
  // first simulated unmount and every response is silently discarded.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const setters = useRef(null);
  setters.current = {
    slides: setSlides, features: setFeatures, services: setServicess,
    articles: setHomeArticles, popup: setPopups,
    partenaires: setPartenaires, chambres: setChambres,
  };

  const load = useCallback((key) => {
    setStatus((s) => (s[key] === "loading" ? s : { ...s, [key]: "loading" }));
    return axios
      .get(`${BASE_URL}${SECTION_ENDPOINTS[key]}`)
      .then((res) => {
        if (!alive.current) return;
        setters.current[key](Array.isArray(res.data) ? res.data : []);
        setStatus((s) => ({ ...s, [key]: "ready" }));
      })
      .catch((err) => {
        if (!alive.current) return;
        console.error(`Home: section "${key}" failed to load —`, err);
        setStatus((s) => ({ ...s, [key]: "error" }));
      });
  }, []);

  useEffect(() => {
    // Above the fold: these decide what the visitor sees in the first second.
    load("slides"); load("features"); load("services"); load("articles");

    // Below the fold: deferred to idle so they never compete with the hero
    // for bandwidth. The timeout is the backstop for browsers that stay busy.
    const runDeferred = () => {
      load("popup"); load("partenaires"); load("chambres");
    };
    const canIdle = typeof window.requestIdleCallback === "function";
    const handle = canIdle
      ? window.requestIdleCallback(runDeferred, { timeout: 1800 })
      : setTimeout(runDeferred, 250);

    return () => {
      if (canIdle) window.cancelIdleCallback(handle);
      else clearTimeout(handle);
    };
  }, [load]);

  // ─── Hero: autoplay, swipe, keyboard ────────────────────────────────────

  const startAutoplay = useCallback(() => {
    clearInterval(slideInterval.current);
    if (slides.length <= 1) return;
    slideInterval.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
  }, [slides.length]);

  useEffect(() => {
    startAutoplay();
    return () => clearInterval(slideInterval.current);
  }, [startAutoplay]);

  // A carousel that keeps turning in a background tab is wasted work and
  // means the visitor comes back to a slide they never saw start.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) clearInterval(slideInterval.current);
      else startAutoplay();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [startAutoplay]);

  const goToSlide = useCallback((index) => {
    setActiveSlide(index);
    startAutoplay();
  }, [startAutoplay]);

  const prevSlide = () => goToSlide((activeSlide - 1 + slides.length) % slides.length);
  const nextSlide = () => goToSlide((activeSlide + 1) % slides.length);

  // Swipe. The whole point of a phone: the hero should answer to the thumb.
  const onTouchStart = (e) => {
    const p = e.touches[0];
    touchStart.current = { x: p.clientX, y: p.clientY, at: Date.now() };
  };
  const onTouchEnd = (e) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || slides.length <= 1) return;
    const p = e.changedTouches[0];
    const dx = p.clientX - start.x;
    const dy = p.clientY - start.y;
    // Horizontal intent only, and not a slow drag — otherwise it's a scroll.
    if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.6) return;
    if (Date.now() - start.at > 700) return;
    const forward = isRtl ? dx > 0 : dx < 0;
    goToSlide(
      forward
        ? (activeSlide + 1) % slides.length
        : (activeSlide - 1 + slides.length) % slides.length
    );
  };

  const onHeroKey = (e) => {
    if (slides.length <= 1) return;
    if (e.key === "ArrowRight") { e.preventDefault(); isRtl ? prevSlide() : nextSlide(); }
    if (e.key === "ArrowLeft")  { e.preventDefault(); isRtl ? nextSlide() : prevSlide(); }
  };

  // ─── Features: the tabs advance on their own ────────────────────────────
  // A timeout rather than an interval, and keyed on activeFeature, so the
  // three seconds restart from zero every time the pane changes. With an
  // interval, clicking a tab one second before the next tick would tear the
  // panel away almost immediately — the visitor's click would look ignored.
  useEffect(() => {
    if (featPaused || featHidden) return;
    if (features.length <= 1) return;
    // The rest of the page honours this, and a panel that swaps itself out
    // every three seconds is exactly the motion the setting asks us to drop.
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) return;

    const timer = setTimeout(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearTimeout(timer);
  }, [activeFeature, featPaused, featHidden, features.length]);

  // Same reasoning as the hero carousel: nothing should be rotating through
  // panes nobody is looking at.
  useEffect(() => {
    const onVisibility = () => setFeatHidden(document.hidden);
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Below 992px the tabs are a horizontal strip with the scrollbar hidden,
  // and the strip does not move on its own. Rotating to the fourth feature
  // left the visitor looking at the first pill, still unhighlighted: the
  // panel changed under a row of tabs that appeared not to have changed at
  // all. The strip now follows the active tab.
  //
  // Deliberately NOT scrollIntoView: that walks up the ancestor chain and
  // will scroll the page vertically to reach the section. scrollBy on the
  // strip itself moves nothing else. And it takes a delta measured from
  // getBoundingClientRect rather than offsetLeft, because scrollLeft runs
  // negative in RTL on some engines and positive on others — a relative
  // physical delta is the one number that means the same thing in Arabic.
  useEffect(() => {
    const strip = featTabsRef.current;
    if (!strip || features.length <= 1) return;
    // From 992px the strip is a vertical column with overflow visible, so
    // there is nothing to scroll — this check is the media query.
    if (strip.scrollWidth <= strip.clientWidth + 1) return;

    const tab = strip.querySelectorAll(".feat-tab")[activeFeature];
    if (!tab) return;

    const stripBox = strip.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    const delta =
      (tabBox.left + tabBox.width / 2) - (stripBox.left + stripBox.width / 2);
    if (Math.abs(delta) < 4) return;   // already centred; don't nudge

    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    try {
      strip.scrollBy({ left: delta, behavior: reduce ? "auto" : "smooth" });
    } catch (e) {
      strip.scrollLeft += delta;   // older Safari: no options object
    }
  }, [activeFeature, features.length, isRtl]);

  // The touch pause below is armed from a handler, so it has to be disarmed
  // here or a resume can fire into an unmounted component.
  useEffect(() => () => clearTimeout(featTouchTimer.current), []);

  // A shrinking list (a reload returning fewer features) would otherwise
  // leave activeFeature pointing past the end, and every pane hidden.
  useEffect(() => {
    if (features.length && activeFeature > features.length - 1) setActiveFeature(0);
  }, [features.length, activeFeature]);

  // ─── Swiper initialization ──────────────────────────────────────────────
  // Each slider is built when its own data arrives, not when the whole page
  // finishes loading, and only if there is something to put in it.

  useEffect(() => {
    if (typeof window.Swiper === "undefined") return;
    if (!partenaires.length || !document.querySelector("#partenairesSwiper")) return;
    partenairesSwiper.current = new window.Swiper("#partenairesSwiper", {
      rtl: isRtl,
      slidesPerView: 1.12, spaceBetween: 14, loop: true,
      watchSlidesProgress: true,
      grabCursor: true,
      autoplay: { delay: 3400, disableOnInteraction: false, pauseOnMouseEnter: true },
      navigation: {
        nextEl: ".swiper-button-next-partenaires",
        prevEl: ".swiper-button-prev-partenaires",
      },
      breakpoints: {
        480: { slidesPerView: 1.35, spaceBetween: 16 },
        768: { slidesPerView: 2.2, spaceBetween: 20 },
        992: { slidesPerView: 3, spaceBetween: 24 },
        1400: { slidesPerView: 4, spaceBetween: 26 },
      },
    });
    return () => {
      if (partenairesSwiper.current) {
        partenairesSwiper.current.destroy(true, true);
        partenairesSwiper.current = null;
      }
    };
  }, [partenaires, isRtl]);

  useEffect(() => {
    if (typeof window.Swiper === "undefined") return;
    if (!chambres.length || !document.querySelector("#chambresSwiper")) return;
    chambresSwiper.current = new window.Swiper("#chambresSwiper", {
      rtl: isRtl,
      slidesPerView: 2.15, spaceBetween: 14, loop: true,
      grabCursor: true,
      navigation: {
        nextEl: ".swiper-button-next-chambres",
        prevEl: ".swiper-button-prev-chambres",
      },
      breakpoints: {
        576: { slidesPerView: 3.2, spaceBetween: 16 },
        768: { slidesPerView: 4, spaceBetween: 18 },
        992: { slidesPerView: 5, spaceBetween: 20 },
        1200: { slidesPerView: 6, spaceBetween: 22 },
      },
    });
    return () => {
      if (chambresSwiper.current) {
        chambresSwiper.current.destroy(true, true);
        chambresSwiper.current = null;
      }
    };
  }, [chambres, isRtl]);

  // Pause the partners autoplay while a card is expanded into the full panel.
  useEffect(() => {
    const onPartnerExpand = (e) => {
      const s = partenairesSwiper.current;
      if (!s || !s.autoplay) return;
      if (e.detail && e.detail.open) s.autoplay.stop(); else s.autoplay.start();
    };
    window.addEventListener("caci:partner-expand", onPartnerExpand);
    return () => window.removeEventListener("caci:partner-expand", onPartnerExpand);
  }, []);

  // ─── Scroll reveal — re-scans whenever new content lands ────────────────
  const pageRef = useReveal([status, slides, features, services, homeArticle, partenaires, chambres, activeFeature]);

  // ─── Popup logic ────────────────────────────────────────────────────────

  useEffect(() => {
    let timer;
    if (status.popup === "ready" && popup.length > 0 && !hasShownPopup) {
      timer = setTimeout(() => setShowPopup(true), 1100);
    }
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popup, status.popup, hasShownPopup]);

  const handleClosePopup = () => { setShowPopup(false); setHasShownPopup(true); };

  // A modal that traps scroll behind it, and closes on Esc like every other
  // dialog the visitor has ever used.
  useEffect(() => {
    if (!showPopup) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") handleClosePopup(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopup]);

  // ─── Render: Popup ──────────────────────────────────────────────────────

  const renderPopup = () => {
    if (!showPopup || !Array.isArray(popup) || popup.length === 0) return null;
    const p = popup[0];
    return (
      <div className="caci-pop-backdrop" onClick={handleClosePopup}>
        <div
          className="caci-pop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="caci-pop-title"
          dir={isRtl ? "rtl" : "ltr"}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="caci-pop-grip" />
          <div className="caci-pop-head">
            <h5 id="caci-pop-title">{getTitleslide(p)}</h5>
            <button className="caci-pop-x" onClick={handleClosePopup} aria-label={t("home.Close")}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div className="caci-pop-body">
            {p?.image && <img src={`${BASE_URL}${p.image}`} alt={getTitleslide(p)} />}
          </div>
          <div className="caci-pop-actions">
            <button className="caci-pop-secondary" onClick={handleClosePopup}>
              {t("home.Close")}
            </button>
            {p?.link_url &&
              (p.link_url.startsWith("http") ? (
                <a href={p.link_url} className="caci-pop-primary" target="_blank" rel="noopener noreferrer">
                  {getLinkTextslide(p) || t("home.Read_More")}
                  <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
                </a>
              ) : (
                <Link to={p.link_url} className="caci-pop-primary" onClick={handleClosePopup}>
                  {getLinkTextslide(p) || t("home.Read_More")}
                  <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
                </Link>
              ))}
          </div>
        </div>
      </div>
    );
  };

  // ─── Render: Slide CTA ──────────────────────────────────────────────────

  const renderSlideLink = (slide) => {
    if (!slide?.link_url) return null;
    const isExternal = slide.link_url.startsWith("http");
    const label = getLinkTextslide(slide) || t("home.Read_More");
    const content = (
      <>
        {label}
        <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
      </>
    );
    return isExternal ? (
      <a href={slide.link_url} className="hero-cta" target="_blank" rel="noopener noreferrer">{content}</a>
    ) : (
      <Link to={slide.link_url} className="hero-cta">{content}</Link>
    );
  };

  // ─── Section state helper ───────────────────────────────────────────────
  // One place decides what a band shows, so no section can drift into its own
  // idea of what "empty" looks like.

  const emptyText = t("home.section_empty", "Rien à afficher ici pour le moment.");
  const errorText = t("home.section_error", "Cette section n'a pas pu être chargée.");
  const retryText = t("home.retry", "Réessayer");

  const renderSection = (key, items, { skeleton, content, variant }) => {
    if (status[key] === "loading") return skeleton;
    if (status[key] === "error" || items.length === 0) {
      return (
        <SectionNote
          state={status[key] === "error" ? "error" : "empty"}
          onRetry={() => load(key)}
          emptyText={emptyText}
          errorText={errorText}
          retryText={retryText}
          variant={variant}
        />
      );
    }
    return content;
  };

  // ─── Main render ────────────────────────────────────────────────────────

  return (
    <div className={`caci-home${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <style>{GLOBAL_STYLES}</style>
      <style>{PORTAL_STYLES}</style>

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      {renderPopup()}

      {/* ══════════════════ HERO ══════════════════ */}
      <section
        id="hero"
        className="caci-hero dark-background"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onKeyDown={onHeroKey}
        tabIndex={slides.length > 1 ? 0 : -1}
        aria-roledescription="carousel"
        aria-label="CACI"
      >
        {status.slides === "loading" && <SkelHero />}

        {status.slides !== "loading" && slides.length === 0 && (
          <SectionNote
            state={status.slides === "error" ? "error" : "empty"}
            onRetry={() => load("slides")}
            emptyText={emptyText}
            errorText={errorText}
            retryText={retryText}
            variant="hero"
          />
        )}

        {slides.map((slide, index) => {
          const imgSrc = getImageslide(slide);
          const isActive = index === activeSlide;
          const title = getTitleslide(slide);
          const words = String(title || "").split(/\s+/).filter(Boolean);
          return (
            <div
              key={slide?.id || index}
              className={`hero-slide${isActive ? " is-active" : ""}`}
              aria-hidden={!isActive}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} / ${slides.length}`}
            >
              {imgSrc && (
                <img
                  src={imgSrc}
                  alt=""
                  className="hero-img"
                  loading={index === 0 ? "eager" : "lazy"}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.parentElement.style.background =
                      "linear-gradient(140deg, #0F4A30 0%, #08150E 100%)";
                  }}
                />
              )}
              <div className="hero-scrim" />
              <div className="hero-caption">
                <div className="caci-shell">
                  <div className="hero-box">
                    <span className="hero-eyebrow">CACI</span>
                    <h2 className="hero-title" dir="auto">
                      {words.length
                        ? words.map((w, i) => (
                            <React.Fragment key={i}>
                              <span className="w" style={{ "--i": i }}>{w}</span>
                              {i < words.length - 1 ? " " : ""}
                            </React.Fragment>
                          ))
                        : "\u00A0"}
                    </h2>
                    <p className="hero-text" dir="auto">{getDescriptionslide(slide) || "\u00A0"}</p>
                    {renderSlideLink(slide)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {slides.length > 1 && (
          <>
            <div className="hero-dots" role="tablist" aria-label="Slides">
              {slides.map((s, i) => (
                <button
                  key={s?.id || i}
                  type="button"
                  role="tab"
                  aria-selected={i === activeSlide}
                  aria-label={getTitleslide(s) || `Slide ${i + 1}`}
                  onClick={() => goToSlide(i)}
                  className={`hero-dot${i === activeSlide ? " is-active" : ""}`}
                />
              ))}
            </div>

            <button onClick={prevSlide} className="hero-nav is-prev" aria-label="Previous slide">
              <i className={`bi bi-chevron-${isRtl ? "right" : "left"}`} style={{ fontSize: 20 }} />
            </button>
            <button onClick={nextSlide} className="hero-nav is-next" aria-label="Next slide">
              <i className={`bi bi-chevron-${isRtl ? "left" : "right"}`} style={{ fontSize: 20 }} />
            </button>
          </>
        )}

        <div className="hero-cue" aria-hidden="true">
          <i />
          <Seal size={16} spin />
        </div>

        <div className="hero-waves" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
               viewBox="0 24 150 28" preserveAspectRatio="none">
            <defs>
              <path id="wave-path" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
            </defs>
            <g className="w1"><use xlinkHref="#wave-path" x={50} y={3} /></g>
            <g className="w2"><use xlinkHref="#wave-path" x={50} y={0} /></g>
            <g className="w3"><use xlinkHref="#wave-path" x={50} y={9} /></g>
          </svg>
        </div>
      </section>

      {/* ══════════════════ ABOUT ══════════════════ */}
      <section id="caci-about">
        <div className="caci-shell">
          <SectionHead label={t("home.About")} title={t("home.Who_we_are")} />
          <div className="about-grid">
            <div className="about-col-1">
              <p className="about-lead styled-translation" data-rv="rise">{t("home.About_txt_01")}</p>
              <ul className="about-list">
                {[t("home.opt01"), t("home.opt02"), t("home.opt03")].map((opt, i) => (
                  <li key={i} data-rv="rise" style={{ "--i": i + 1 }}>
                    <span className="about-check"><i className="bi bi-check2" /></span>
                    <span>{opt}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="about-col-2" data-rv="rise" style={{ "--i": 2 }}>
              <p className="about-body styled-translation">{t("home.About_txt_02")}</p>
            </div>
          </div>
        </div>
      </section>

      
      {/* ══════════════════ ARTICLES ══════════════════ */}
      <section id="caci-articles">
        <div className="caci-shell">
          <SectionHead
            label={t("home.ArticleRecent_01")}
            title={t("home.ArticleRecent_01")}
            sub={t("home.ArticleRecent_02")}
            action={
              <Link to={langPath("/toutacticle")} className="sec-more" data-discover="true">
                {t("home.see_More")}
                <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
              </Link>
            }
          />

          {renderSection("articles", homeArticle, {
            skeleton: <Rail columns={3} label={t("home.ArticleRecent_01")}><SkelCards count={3} /></Rail>,
            content: (
          <Rail columns={3} label={t("home.ArticleRecent_01")}>
            {homeArticle.map((article, index) => (
                <div
                  key={article.id || `article-${index}`}
                  data-rv="rise"
                  style={{ "--i": index % 3 }}
                >
                  <article className="art-card">
                    <div className="art-media">
                      <img
                        src={getImagehomeArticle(article)}
                        alt={getTitlehomeArticle(article)}
                        loading="lazy"
                        onError={(e) => { e.target.src = "/api/placeholder/800/600"; }}
                      />
                      <span className="art-date">
                        <i className="bi bi-calendar3" />
                        <time dateTime={article.starts_at}>{formatDate(article.starts_at)}</time>
                      </span>
                    </div>
                    <div className="art-body">
                      {article.cat_articles?.length > 0 && (
                        <div className="art-cats">
                          {article.cat_articles
                            .filter((ca) => ca?.category)
                            .map((ca, idx) => (
                              <Link
                                key={ca.category?.id || `cat-${idx}`}
                                to={`/category/${ca.category.id}`}
                                className="art-cat"
                              >
                                {getTitlehomeArticle(ca.category)}
                              </Link>
                            ))}
                        </div>
                      )}
                      <Link to={`/article/${article.id}/${article.slug}`} className="art-title">
                        <span>{getTitlehomeArticle(article)}</span>
                      </Link>
                      <span className="art-foot">
                        {t("home.Read_More")}
                        <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
                      </span>
                    </div>
                  </article>
                </div>
              ))}
          </Rail>
            ),
          })}
        </div>
      </section>


      {/* ══════════════════ EVENTS ══════════════════ */}
      <HomeEventsSection limit={1} />

      {/* ══════════════════ FEATURES (tabs) ══════════════════ */}
      <section id="caci-features">
        <div className="caci-shell">
          <SectionHead label={t("home.Services_txt_01")} title={t("home.Services_txt_01")} />

          {status.features === "loading" && <SkelFeatures />}

          {status.features !== "loading" && features.length === 0 && (
            <SectionNote
              state={status.features === "error" ? "error" : "empty"}
              onRetry={() => load("features")}
              emptyText={emptyText}
              errorText={errorText}
              retryText={retryText}
            />
          )}

          {features.length > 0 && (
          <div
            className="feat-layout"
            /* pointerType is checked because a tap on a touchscreen fires a
               synthetic pointerenter with no matching leave, which would park
               the rotation for good on a phone. Tapping a tab already resets
               the countdown, so touch needs nothing else. */
            onPointerEnter={(e) => { if (e.pointerType === "mouse") setFeatPaused(true); }}
            onPointerLeave={(e) => { if (e.pointerType === "mouse") setFeatPaused(false); }}
            /* Keyboard equivalent: React maps these to focusin/focusout, so
               tabbing into any tab or link inside the block holds it too. */
            onFocus={() => setFeatPaused(true)}
            onBlur={() => setFeatPaused(false)}
            /* Touch has no hover to pause on, and now that the strip scrolls
               itself the timer would yank it out from under a finger that is
               dragging it. Hold while touched, and for a few seconds after —
               someone who just swiped the tabs is still reading. */
            onTouchStart={() => {
              clearTimeout(featTouchTimer.current);
              setFeatPaused(true);
            }}
            onTouchEnd={() => {
              clearTimeout(featTouchTimer.current);
              featTouchTimer.current = setTimeout(() => setFeatPaused(false), 4000);
            }}
            onTouchCancel={() => {
              clearTimeout(featTouchTimer.current);
              featTouchTimer.current = setTimeout(() => setFeatPaused(false), 4000);
            }}
          >
            {(
              <div
                className="feat-tabs"
                ref={featTabsRef}
                role="tablist"
                aria-label={t("home.Services_txt_01")}
              >
                {features.map((feature, index) => (
                  <button
                    key={feature.id}
                    type="button"
                    role="tab"
                    aria-selected={index === activeFeature}
                    className={`feat-tab${index === activeFeature ? " is-active" : ""}`}
                    onClick={() => setActiveFeature(index)}
                  >
                    <i className={`bi ${feature.iconInput || "bi-star"}`} />
                    <span>{getTitlehomeArticle(feature)}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="feat-panes">
              {features.map((feature, index) => (
                <div
                  key={feature.id}
                  className={`feat-pane${index === activeFeature ? " is-active" : ""}`}
                  role="tabpanel"
                >
                  <div className="feat-pane-inner">
                    <h3 className="feat-title" style={{ "--i": 0 }}>{getTitlefeature(feature)}</h3>

                    {getImagefeature(feature) && (
                      <div className="feat-media">
                        <img src={getImagefeature(feature)} alt={getTitlefeature(feature)} loading="lazy" />
                      </div>
                    )}

                    <div className="feat-copy">
                      <div
                        className="feat-desc"
                        style={{ "--i": 1 }}
                        dangerouslySetInnerHTML={{ __html: getShortDescriptionFeature(feature) }}
                      />
                      {feature.points && (
                        <ul className="feat-points" style={{ "--i": 2 }}>
                          {feature.points.map((point, i) => (
                            <li key={i}>
                              <i className="bi bi-check2-all" />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {feature.extraDescription && (
                        <p className="feat-desc" style={{ "--i": 3 }}>{feature.extraDescription}</p>
                      )}
                      <Link
                        to={`/page/${feature.id}/${feature.slug}`}
                        className="feat-btn"
                        style={{ "--i": 4 }}
                        data-discover="true"
                      >
                        {t("home.Read_More")}
                        <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </section>

      {/* ══════════════════ SERVICES ══════════════════ */}
      <section id="caci-services">
        <div className="caci-shell">
          <SectionHead
            tone="dark"
            label={t("home.Services_txt_01")}
            title={t("home.Services_txt_01")}
            sub={t("home.Services_txt_02")}
          />

          {renderSection("services", services, {
            skeleton: <Rail columns={4} label={t("home.Services_txt_01")}><SkelCards count={4} /></Rail>,
            content: (
          <Rail columns={4} label={t("home.Services_txt_01")}>
            {services.map((service, index) => (
              <div key={service.id} data-rv="rise" style={{ "--i": index % 4 }}>
                <Link to={`/page/${service.id}/${service.slug}`} className="svc-card">
                  {getImagefeature(service) && (
                    <div className="svc-media">
                      <img
                        src={getImagefeature(service)}
                        alt={getTitlehomeArticle(service)}
                        loading="lazy"
                        onError={(e) => { e.target.src = "/api/placeholder/600/400"; }}
                      />
                      <span className="svc-icon">
                        <i className={`bi ${service.iconInput || "bi-activity"}`} />
                      </span>
                    </div>
                  )}
                  <div className="svc-body">
                    <h3 className="svc-title">{getTitlehomeArticle(service)}</h3>
                    <div
                      className="svc-desc"
                      dangerouslySetInnerHTML={{ __html: getShortDescriptionService(service) }}
                    />
                    <span className="svc-link">
                      {t("home.Read_More")}
                      <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </Rail>
            ),
          })}
        </div>
      </section>

      {/* ══════════════════ PARTNERS ══════════════════ */}
      <section id="caci-partners" className={isRtl ? "partners-rtl" : ""}>
        <div className="caci-shell">
          <SectionHead
            label={t("home.OurPartners01")}
            title={t("home.OurPartners")}
            action={
              <Link to={langPath("/partenaire")} className="sec-more">
                {t("home.see_More")}
                <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
              </Link>
            }
          />

          {renderSection("partenaires", partenaires, {
            skeleton: <Rail columns={4} label={t("home.OurPartners")}><SkelCards count={4} withBar /></Rail>,
            content: (
          <div className="swiper" id="partenairesSwiper" dir={isRtl ? "rtl" : "ltr"}>
            <div className="swiper-wrapper">
              {partenaires.map((partner) => {
                const imgSrc = partner.image ? `${BASE_URL}${partner.image}` : null;
                const title = getTitleslide(partner);
                const desc = getDescriptionslide(partner) || getTitleslide(partner);
                const url = partner.link_url || "https://caci.dz/";
                const displayUrl = url.replace(/^https?:\/\//, "").replace(/\/$/, "");
                return (
                  <div key={partner.id} className="swiper-slide">
                    <PartnerCard
                      url={url}
                      imgSrc={imgSrc}
                      title={title}
                      desc={desc}
                      displayUrl={displayUrl}
                      ctaLabel={t("home.see_More") || "Visiter"}
                      isRtl={isRtl}
                    />
                  </div>
                );
              })}
            </div>
            <div className="swiper-button-prev swiper-button-prev-partenaires" />
            <div className="swiper-button-next swiper-button-next-partenaires" />
          </div>
            ),
          })}
        </div>
      </section>

      {/* ══════════════════ CHAMBRES ══════════════════ */}
      <section id="caci-chambres">
        <div className="caci-shell">
          <SectionHead
            label={t("home.Ourchambress")}
            title={t("home.Ourchambress")}
            action={
              <Link to={langPath("/chambre_de_commerces")} className="sec-more" data-discover="true">
                {t("home.see_More")}
                <i className={`bi bi-arrow-${isRtl ? "left" : "right"}`} />
              </Link>
            }
          />

          {renderSection("chambres", chambres, {
            skeleton: <SkelChambres count={6} />,
            content: (
          <div className="swiper" id="chambresSwiper" dir={isRtl ? "rtl" : "ltr"}>
            <div className="swiper-wrapper">
              {chambres.map((chamber) => (
                <div key={chamber.id} className="swiper-slide">
                  <Link
                    to={`/chambre_de_commerce/${chamber.id}/${chamber.name}`}
                    className="chambre-card"
                  >
                    <span className="chambre-ring">
                      <img
                        src={`${BASE_URL}/assets/entreprise/${chamber.logo}`}
                        alt={chamber.name}
                        loading="lazy"
                        onError={(e) => { e.target.src = "/api/placeholder/80/80"; }}
                      />
                    </span>
                    <span className="chambre-name">{getTitlehomeArticle(chamber)}</span>
                  </Link>
                </div>
              ))}
            </div>
            <div className="swiper-button-prev swiper-button-prev-chambres" />
            <div className="swiper-button-next swiper-button-next-chambres" />
          </div>
            ),
          })}
        </div>
      </section>
    </div>
  );
}

export default Main;